import React, { useState, useEffect } from "react";
import padelEagleLogo from "../img/padelEagle.png";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  LinearProgress,
  AppBar,
  Toolbar,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  Event as EventIcon,
  Home as HomeIcon,
  Visibility,
  VisibilityOff,
  AccessTime as ClockIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  Leaderboard as LeaderboardIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useSeason } from "../contexts/SeasonContext";
import { useNavigate } from "react-router-dom";
import {
  Player,
  PlayerHistory,
  LeagueDay,
  PenaltyBonus,
  Badge,
  PopupStyle,
} from "../types";
import { POPUP_STYLE_CONFIG, PopupHeader, openPopupLink } from "../utils/popupStyles";
import { apiRequest } from "../config/api";
import BadgeDisplay from "../components/BadgeDisplay";
import BadgeCelebration from "../components/BadgeCelebration";
import DiscountCodeGiftDialog from "../components/DiscountCodeGiftDialog";
import SeasonSelector from "../components/SeasonSelector";
import PlayerSeasonRegistrationDialog from "../components/PlayerSeasonRegistrationDialog";
import GearOrderEditDialog from "../components/GearOrderEditDialog";
import SeasonRegistrationBanner from "../components/SeasonRegistrationBanner";
import PrivacyPolicyAcceptanceDialog from "../components/PrivacyPolicyAcceptanceDialog";
import InvitationManager from "../components/InvitationManager";
import TournamentPerformanceSection from "../components/TournamentPerformanceSection";
import CountdownTimer from "../components/CountdownTimer";
import VenueBadge from "../components/VenueBadge";
import PublicTournaments from "./PublicTournaments";
import { useOrganization } from "../contexts/OrganizationContext";
import { trackEvent } from "../utils/analytics";
import { formatLeagueDayDateShort, formatLeagueDayDateCustom } from "../utils/dateFormat";

// ── Ranking Insights helpers ────────────────────────────────────────────────
const RANKING_PARTICIPATION_WEIGHT = 40;
const GAME_CONFIDENCE = 80;
const MIN_WINS_THRESHOLD = 5;

interface RankingPlayer {
  playerId: string;
  playerName: string;
  gamesWon: number;
  gamesPlayed: number;
  totalPoints: number;
  bayesianWinRate?: number;
  simScore?: number;
}

// deltas: map of playerId → { extraWins, extraLosses } to apply before recomputing
// delta tracks individual game counts, matching how the backend accumulates stats
type GameDelta = { gamesWonDelta: number; gamesLostDelta: number };

// ── Betting on the upcoming league day ──────────────────────────────────────
interface BetEligibility {
  eligible: boolean;
  reason?: string;
  courtCount: number;
  deadlinePassed: boolean;
  isCompleted: boolean;
}
interface Bet {
  id: string;
  leagueDayId: string;
  predictions: Record<string, { pair1Games: number; pair2Games: number }>;
  settled: boolean;
  bonusPoints?: number;
  matchResults?: Record<string, boolean>;
  roundResults?: { roundNumber: number; hit: boolean }[];
}
interface BetInfo {
  leagueDayId: string;
  eligibility: BetEligibility;
  bet: Bet | null;
}
interface BetLeaderboardEntry {
  playerId: string;
  playerName: string;
  bonusPoints: number;
  roundResults: { roundNumber: number; hit: boolean }[];
}

// "1, 3 and 4" — used to phrase which rounds a bettor predicted exactly correct
function formatRoundsList(rounds: number[]): string {
  const sorted = [...rounds].sort((a, b) => a - b);
  if (sorted.length === 1) return `Round ${sorted[0]}`;
  return `Rounds ${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`;
}

function buildBetResultsMarqueeText(results: BetLeaderboardEntry[]): string | null {
  if (results.length === 0) return null;
  const winners = results.filter(r => r.bonusPoints > 0);
  if (winners.length === 0) {
    return '😅 Nobody predicted a round completely correct on this league day\'s bets!';
  }
  return winners
    .map(w => {
      const hitRounds = w.roundResults.filter(rr => rr.hit).map(rr => rr.roundNumber);
      return `🎉 ${w.playerName} got ${w.bonusPoints} point${w.bonusPoints === 1 ? '' : 's'} for their bet — predicted ${formatRoundsList(hitRounds)} completely correct!`;
    })
    .join('     •     ');
}

function recomputeHybridRankings(
  players: RankingPlayer[],
  deltas: Record<string, GameDelta>,
): RankingPlayer[] {
  const volumeFactor = RANKING_PARTICIPATION_WEIGHT / 100;
  const qualityFactor = 1 - volumeFactor;

  const modified: RankingPlayer[] = players.map(p => {
    const d = deltas[p.playerId];
    return d
      ? { ...p,
          gamesWon: p.gamesWon + d.gamesWonDelta,
          gamesLost: (p.gamesLost ?? 0) + d.gamesLostDelta,
          gamesPlayed: p.gamesPlayed + d.gamesWonDelta + d.gamesLostDelta,
          totalPoints: p.totalPoints + d.gamesWonDelta,
        }
      : { ...p };
  });

  // Recompute Bayesian win rates
  const totalWins = modified.reduce((s, p) => s + p.gamesWon, 0);
  const totalGames = modified.reduce((s, p) => s + p.gamesPlayed, 0);
  const divAvg = totalGames > 0 ? totalWins / totalGames : 0.5;
  modified.forEach(p => {
    p.bayesianWinRate = p.gamesPlayed > 0
      ? Math.round(((GAME_CONFIDENCE * divAvg + p.gamesWon) / (GAME_CONFIDENCE + p.gamesPlayed)) * 10000) / 100
      : Math.round(divAvg * 10000) / 100;
  });

  // Volume score (0-100 normalized within division)
  const active = modified.filter(p => p.gamesPlayed > 0);
  const pointsList = active.map(p => p.totalPoints).sort((a, b) => a - b);
  const maxPoints = pointsList[pointsList.length - 1] || 0;
  const maxWins = active.reduce((m, p) => Math.max(m, p.gamesWon), 0);
  const minPoints = maxWins >= MIN_WINS_THRESHOLD ? Math.max(pointsList[0] || 0, MIN_WINS_THRESHOLD) : 0;
  const range = maxPoints - minPoints;

  modified.forEach(p => {
    if (p.gamesPlayed === 0) { p.simScore = 0; return; }
    const vol = range > 0 ? Math.max(0, ((p.totalPoints - minPoints) / range) * 100) : 50;
    const qual = p.bayesianWinRate || 0; // raw Bayesian win rate, not normalized
    p.simScore = Math.round((vol * volumeFactor + qual * qualityFactor) * 100) / 100;
  });

  return [...modified].sort((a, b) => {
    if (a.gamesPlayed === 0 && b.gamesPlayed > 0) return 1;
    if (b.gamesPlayed === 0 && a.gamesPlayed > 0) return -1;
    const diff = (b.simScore || 0) - (a.simScore || 0);
    return Math.abs(diff) > 0.01 ? diff : b.gamesPlayed - a.gamesPlayed;
  });
}

/** Returns the minimum extra wins needed to rank ahead of rivalId. */
function minWinsToOvertake(players: RankingPlayer[], myId: string, rivalId: string): number {
  for (let extra = 1; extra <= 20; extra++) {
    const sim = recomputeHybridRankings(players, { [myId]: { gamesWonDelta: extra, gamesLostDelta: 0 } });
    const myPos = sim.findIndex(p => p.playerId === myId);
    const rivalPos = sim.findIndex(p => p.playerId === rivalId);
    if (myPos < rivalPos) return extra;
  }
  return 21;
}
// ───────────────────────────────────────────────────────────────────────────

function PlayerDashboard() {
  const { playerUser, playerLogout, playerToken } = useAuth();
  const { selectedSeason, allSeasons, activeSeason } = useSeason();
  const { currentOrg } = useOrganization();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [playerHistory, setPlayerHistory] = useState<PlayerHistory | null>(
    null,
  );
  const [announcement, setAnnouncement] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [leagueDays, setLeagueDays] = useState<LeagueDay[]>([]);
  const [, setTimerUpdate] = useState(0);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [hasRegistered, setHasRegistered] = useState(false);
  const [seasonName, setSeasonName] = useState("Summer 2026");
  const [registrationOpenDate, setRegistrationOpenDate] = useState<Date | null>(null);
  const [isSeasonRegistrationOpen, setIsSeasonRegistrationOpen] =
    useState(false);
  const [midSeasonIsOpen, setMidSeasonIsOpen] = useState(false);
  const [gearEditingEnabled, setGearEditingEnabled] = useState(false);
  const [gearOrderDialogOpen, setGearOrderDialogOpen] = useState(false);
  const [penaltiesAndBonuses, setPenaltiesAndBonuses] = useState<
    PenaltyBonus[]
  >([]);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  // Padel Eagle welcome dialog: "Read later" only hides it for this visit; "OK" persists it on the player.
  const [welcomeDialogHidden, setWelcomeDialogHidden] = useState(false);
  const [tournamentTab, setTournamentTab] = useState(0);
  const [pendingInvitationCount, setPendingInvitationCount] = useState(0);
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);

  // Received tournament invitations popup
  const [receivedInvites, setReceivedInvites] = useState<any[]>([]);
  const [invitePopupIndex, setInvitePopupIndex] = useState(0);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  // Bumped after the popup accepts/declines so the invitation list and tournament cards reload
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [tournamentsRefreshKey, setTournamentsRefreshKey] = useState(0);
  const [unseenBadges, setUnseenBadges] = useState<Badge[]>([]);
  const [dashboardView, setDashboardView] = useState<"league" | "tournament">(
    "league",
  );
  // Tournament-only accounts (created via tournament registration, never registered
  // for a league season) have no league access — always force the tournament view.
  const effectiveDashboardView = player?.isTournamentOnly
    ? "tournament"
    : dashboardView;
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  // Ranking Insights
  const [divisionRankings, setDivisionRankings] = useState<any[]>([]);
  const [lastLeagueDayMatches, setLastLeagueDayMatches] = useState<any[]>([]);
  const [lastLeagueDayDate, setLastLeagueDayDate] = useState<string | null>(null);
  const [lastLeagueDayVenue, setLastLeagueDayVenue] = useState<{ venueName?: string; venueAddress?: string } | null>(null);
  // scoreOverrides: matchId → { pair1Games, pair2Games } editable by the player
  const [scoreOverrides, setScoreOverrides] = useState<Record<string, { pair1Games: number; pair2Games: number }>>({});
  const [upcomingLeagueDayMatches, setUpcomingLeagueDayMatches] = useState<any[]>([]);
  const [upcomingLeagueDayMatchesLoading, setUpcomingLeagueDayMatchesLoading] = useState(true);
  const [upcomingLeagueDayMatchesProgress, setUpcomingLeagueDayMatchesProgress] = useState(0);
  const [upcomingLeagueDayDate, setUpcomingLeagueDayDate] = useState<string | null>(null);
  const [upcomingLeagueDayVenue, setUpcomingLeagueDayVenue] = useState<{ venueName?: string; venueAddress?: string } | null>(null);
  const [upcomingScores, setUpcomingScores] = useState<Record<string, { pair1Games: number; pair2Games: number }>>({});
  const [lastDayTab, setLastDayTab] = useState(0); // 0=scores, 1=ranking (mobile only)
  const [upcomingTab, setUpcomingTab] = useState(0);
  const [showGuestPlayers, setShowGuestPlayers] = useState(false);

  // Betting on the upcoming league day
  const [lastDayBetInfo, setLastDayBetInfo] = useState<BetInfo | null>(null);
  const [leagueDayBetResults, setLeagueDayBetResults] = useState<BetLeaderboardEntry[]>([]);
  const [upcomingBetInfo, setUpcomingBetInfo] = useState<BetInfo | null>(null);
  const [isEditingBet, setIsEditingBet] = useState(false);
  const [betSubmitting, setBetSubmitting] = useState(false);
  const [betSubmitError, setBetSubmitError] = useState<string | null>(null);
  // Set when the player is drawn into another division's upcoming day as a guest —
  // the upcoming "what if" section then shows that division instead of the player's own.
  const [upcomingDivisionInfo, setUpcomingDivisionInfo] = useState<{ division: string; isGuest: boolean } | null>(null);
  const [upcomingDivisionRankings, setUpcomingDivisionRankings] = useState<any[]>([]);

  // All upcoming league days the player is eligible to bet on (their home division's day,
  // plus every other division's day they've been drawn into as a guest) — lets the dropdown
  // switch between them instead of always auto-picking one.
  const [upcomingEligibleEntries, setUpcomingEligibleEntries] = useState<any[]>([]);
  const [selectedUpcomingLeagueDayId, setSelectedUpcomingLeagueDayId] = useState<string>('');

  // All completed league days the player has participated in (any division, including as a
  // guest) — backs the "results" dropdown. Mirrors upcomingDivisionInfo/Rankings for the
  // last-league-day "what if" simulation when the selected day was a guest appearance.
  const [myCompletedLeagueDayOptions, setMyCompletedLeagueDayOptions] = useState<{ id: string; date: string | null; division: string; isBetOnly?: boolean; venueName?: string; venueAddress?: string }[]>([]);
  const [selectedResultsLeagueDayId, setSelectedResultsLeagueDayId] = useState<string>('');
  const [resultsDivisionInfo, setResultsDivisionInfo] = useState<{ division: string; isGuest: boolean } | null>(null);
  const [resultsDivisionRankings, setResultsDivisionRankings] = useState<any[]>([]);

  // Pop-up announcements
  const [pendingPopups, setPendingPopups] = useState<{
    id: string;
    title: string;
    message: string;
    imageUrl?: string;
    linkUrl?: string;
    linkLabel?: string;
    style?: PopupStyle;
  }[]>([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  const acknowledgePopup = async (popupId: string) => {
    try {
      await apiRequest(`/api/announcements/popups/${popupId}/acknowledge`, { method: 'POST' });
    } catch {}
    setCurrentPopupIndex((i) => i + 1);
  };

  // Fetch unread pop-up announcements once the player is logged in
  useEffect(() => {
    if (!playerUser?.playerId) return;
    apiRequest('/api/announcements/popups/unread')
      .then((res) => { if (res.ok) return res.json(); })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setPendingPopups(data);
          setCurrentPopupIndex(0);
        }
      })
      .catch(() => {});
  }, [playerUser?.playerId]);

  // Determine if this is a league organization (shows league-specific features)
  const isLeagueOrg =
    currentOrg?.competitionType === "league" ||
    currentOrg?.competitionType === "both";

  // Theme colors based on organization
  const primaryColor =
    currentOrg?.slug === "padel-eagle" ? "#743444" : "#667eea";
  const secondaryColor =
    currentOrg?.slug === "padel-eagle" ? "#E49421" : "#764ba2";
  // Padel Eagle CI: solid Bordeaux surfaces instead of gradients
  const brandGradient =
    currentOrg?.slug === "padel-eagle"
      ? "#743444"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

  useEffect(() => {
    // Track dashboard view
    if (playerUser?.playerId) {
      trackEvent('dashboard_view', {
        playerId: playerUser.playerId,
        division: playerUser.division || 'unknown',
      });
    }
    
    fetchPlayerProfile();
    fetchOpenLeagueDays();
    fetchUpcomingTournaments();
    checkRegistrationStatus();
    fetchAnnouncement();
    fetchReceivedInvites();
    if (playerUser?.playerId && selectedSeason) {
      fetchPlayerHistory();
      fetchPenaltiesAndBonuses();
    }
    // Force re-render every second for timer
    const interval = setInterval(() => {
      setTimerUpdate((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [playerUser?.playerId, playerUser?.division, selectedSeason]);

  // Re-fetch history when player data loads (needed to resolve past-season division from seasonStats)
  useEffect(() => {
    if (player && playerUser?.playerId && selectedSeason) {
      fetchPlayerHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, selectedSeason?.id]);

  // Fetch full division rankings for Ranking Insights (active season only).
  // Deliberately keyed on activeSeason, not selectedSeason: these cover live/upcoming
  // state (rankings, results, betting) that stays tied to whichever season is actually
  // being played, regardless of which season the player happens to have picked in the
  // season selector to browse historical stats.
  useEffect(() => {
    if (!player?.division || !activeSeason?.id) return;
    fetchDivisionRankings(player.division, activeSeason.id);
    loadResultsSection(player.division, activeSeason.id);
    fetchUpcomingLeagueDayMatches(player.division, activeSeason.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.division, activeSeason?.id]);

  // Check privacy policy acceptance on component mount
  useEffect(() => {
    if (player && !player.privacyPolicyAcceptedAt) {
      setShowPrivacyDialog(true);
    }
  }, [player]);

  // Check if player needs to add email
  useEffect(() => {
    if (player && player.privacyPolicyAcceptedAt && !player.email) {
      // Only show email dialog after privacy policy is accepted
      setShowEmailDialog(true);
    }
  }, [player]);

  const fetchAnnouncement = async () => {
    try {
      const response = await apiRequest(
        "/api/announcements/active/player-dashboard",
      );
      if (response.ok) {
        const data = await response.json();
        setAnnouncement(data?.message ? data : null);
      }
    } catch (error) {
      console.error("Error fetching announcement:", error);
    }
  };

  const fetchPlayerProfile = async () => {
    try {
      const response = await apiRequest("/api/player/me", {
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPlayer(data);

        // Check for unseen badges
        if (data.unseenBadges && data.unseenBadges.length > 0 && data.badges) {
          // Filter badges that are in the unseenBadges array
          const unseenBadgeList = data.badges.filter((badge: Badge) =>
            data.unseenBadges.includes(badge.type),
          );
          if (unseenBadgeList.length > 0) {
            setUnseenBadges(unseenBadgeList);
            setShowBadgeCelebration(true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching player profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistrationStatus = async () => {
    try {
      // Check if player has already registered
      const playerRegResponse = await apiRequest(
        "/api/season-registration/check-player-registration",
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );

      if (playerRegResponse.ok) {
        const data = await playerRegResponse.json();
        setHasRegistered(data.hasRegistered);
        setSeasonName(data.seasonName || "Summer 2026");
      }

      // Check if registration is open
      const statusResponse = await apiRequest(
        "/api/season-registration/status",
      );
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setIsSeasonRegistrationOpen(statusData.isOpen || statusData.midSeasonIsOpen);
        setMidSeasonIsOpen(statusData.midSeasonIsOpen || false);
        setGearEditingEnabled(statusData.gearEditingEnabled || false);
        if (statusData.registrationOpenDate) {
          setRegistrationOpenDate(new Date(statusData.registrationOpenDate));
        }
      }
    } catch (error) {
      console.error("Error checking registration status:", error);
    }
  };

  const fetchPlayerHistory = async () => {
    if (!selectedSeason) return;

    try {
      const params = new URLSearchParams();

      // For a past season, use the division the player was in during that season.
      // For the active season, use their current division.
      const seasonStats = player?.seasonStats?.[selectedSeason.id];
      const divisionForSeason = seasonStats?.division || playerUser?.division || "";
      params.append("division", divisionForSeason);
      params.append("seasonId", selectedSeason.id);

      const response = await apiRequest(
        `/api/public/players/${playerUser?.playerId}/history?${params.toString()}`,
      );
      if (response.ok) {
        const data = await response.json();
        setPlayerHistory(data);
      }
    } catch (error) {
      console.error("Error fetching player history:", error);
    }
  };

  const fetchPenaltiesAndBonuses = async () => {
    if (!selectedSeason || !playerUser?.playerId) return;

    try {
      const response = await apiRequest(
        `/api/penalties?seasonId=${selectedSeason.id}`,
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        // Filter only penalties/bonuses for this player
        const playerPenalties = data.filter(
          (pb: PenaltyBonus) => pb.playerId === playerUser.playerId,
        );
        setPenaltiesAndBonuses(playerPenalties);
      }
    } catch (error) {
      console.error("Error fetching penalties and bonuses:", error);
    }
  };

  const fetchDivisionRankings = async (division: string, seasonId: string) => {
    try {
      const params = new URLSearchParams({ division, seasonId, rankBy: 'hybrid', participationWeight: '40' });
      const res = await apiRequest(`/api/public/rankings?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDivisionRankings(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (e) {
      console.error('Error fetching division rankings for insights', e);
    }
  };

  const fetchBetInfo = async (leagueDayId: string): Promise<BetInfo | null> => {
    try {
      const res = await apiRequest(`/api/bets/me?leagueDayId=${leagueDayId}`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('Error fetching bet info', e);
      return null;
    }
  };

  // Everyone's settled bet outcome for a league day — backs the "who called it" banner.
  const fetchLeagueDayBetResults = async (leagueDayId: string): Promise<BetLeaderboardEntry[]> => {
    try {
      const res = await apiRequest(`/api/bets/league-day/${leagueDayId}/results`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.results || [];
    } catch (e) {
      console.error('Error fetching league day bet results', e);
      return [];
    }
  };

  // Cross-division: every completed league day the player took part in (home division or as
  // a drawn-in guest elsewhere), sourced from /history with no division filter. Backs the
  // "results" dropdown — without this, a guest's bet results on another division's day were
  // unreachable since the rest of the dashboard only ever looks at the player's home division.
  const fetchMyCompletedLeagueDayOptions = async (seasonId: string): Promise<{ id: string; date: string | null; division: string }[]> => {
    try {
      const params = new URLSearchParams({ seasonId });
      const res = await apiRequest(`/api/public/players/${playerUser?.playerId}/history?${params}`);
      if (!res.ok) return [];
      const data = await res.json();
      const options = (data.leagueDays || [])
        .filter((ld: any) => ld.isCompleted && !ld.isDraft)
        .map((ld: any) => {
          // Firestore Timestamps arrive as { _seconds, _nanoseconds } — convert to ISO
          const rawDate = ld.date || ld.scheduledDate || null;
          const isoDate = rawDate?._seconds
            ? new Date(rawDate._seconds * 1000).toISOString()
            : rawDate;
          return { id: ld.id, date: isoDate, division: ld.division, isBetOnly: !!ld.isBetOnly, venueName: ld.venueName, venueAddress: ld.venueAddress };
        })
        .sort((a: any, b: any) => (b.date || '').localeCompare(a.date || ''));
      setMyCompletedLeagueDayOptions(options);
      return options;
    } catch (e) {
      console.error('Error fetching completed league day options', e);
      return [];
    }
  };

  const fetchResultsDivisionRankings = async (division: string, seasonId: string) => {
    try {
      const params = new URLSearchParams({ division, seasonId, rankBy: 'hybrid', participationWeight: '40' });
      const res = await apiRequest(`/api/public/rankings?${params}`);
      if (res.ok) {
        const data = await res.json();
        setResultsDivisionRankings(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (e) {
      console.error('Error fetching guest division rankings for results insights', e);
    }
  };

  const fetchLeagueDayResultsById = async (leagueDayId: string, date: string | null, division: string, homeDivision: string, seasonId: string, venue?: { venueName?: string; venueAddress?: string }) => {
    try {
      setSelectedResultsLeagueDayId(leagueDayId);
      setLastLeagueDayDate(date);
      setLastLeagueDayVenue(venue?.venueName ? venue : null);
      const isGuest = division !== homeDivision;
      setResultsDivisionInfo({ division, isGuest });
      if (isGuest) {
        await fetchResultsDivisionRankings(division, seasonId);
      } else {
        setResultsDivisionRankings([]);
      }
      const matchRes = await apiRequest(`/api/public/league-days/${leagueDayId}/matches`);
      if (matchRes.ok) {
        const data = await matchRes.json();
        // Endpoint returns { matches: [], topPerformers: [], ... }
        setLastLeagueDayMatches(data.matches || []);
        setScoreOverrides({});
      }
      const betInfo = await fetchBetInfo(leagueDayId);
      setLastDayBetInfo(betInfo);
      const betResults = await fetchLeagueDayBetResults(leagueDayId);
      setLeagueDayBetResults(betResults);
    } catch (e) {
      console.error('Error fetching league day results', e);
    }
  };

  const loadResultsSection = async (homeDivision: string, seasonId: string) => {
    const options = await fetchMyCompletedLeagueDayOptions(seasonId);
    if (options.length > 0) {
      const mostRecent = options[0];
      await fetchLeagueDayResultsById(mostRecent.id, mostRecent.date, mostRecent.division, homeDivision, seasonId, { venueName: mostRecent.venueName, venueAddress: mostRecent.venueAddress });
    } else {
      setLastLeagueDayMatches([]);
      setLastLeagueDayDate(null);
      setLastLeagueDayVenue(null);
      setLastDayBetInfo(null);
      setLeagueDayBetResults([]);
      setSelectedResultsLeagueDayId('');
      setResultsDivisionInfo(null);
      setResultsDivisionRankings([]);
    }
  };

  const selectResultsLeagueDay = async (leagueDayId: string) => {
    const opt = myCompletedLeagueDayOptions.find((o) => o.id === leagueDayId);
    if (!opt || !player?.division || !selectedSeason?.id) return;
    await fetchLeagueDayResultsById(opt.id, opt.date, opt.division, player.division, selectedSeason.id, { venueName: opt.venueName, venueAddress: opt.venueAddress });
  };

  const fetchUpcomingDivisionRankings = async (division: string, seasonId: string) => {
    try {
      const params = new URLSearchParams({ division, seasonId, rankBy: 'hybrid', participationWeight: '40' });
      const res = await apiRequest(`/api/public/rankings?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUpcomingDivisionRankings(Array.isArray(data) ? data : (data.data || []));
      }
    } catch (e) {
      console.error('Error fetching guest division rankings for upcoming insights', e);
    }
  };

  const upcomingEntryId = (entry: any): string | undefined => entry.matches[0]?.leagueDayId || entry.leagueDay?.id;

  const applyUpcomingEntry = async (target: any, homeDivision: string, seasonId: string) => {
    const targetDivision = target.leagueDay.division;
    const isGuest = targetDivision !== homeDivision;
    setSelectedUpcomingLeagueDayId(upcomingEntryId(target) || '');
    setUpcomingDivisionInfo({ division: targetDivision, isGuest });
    setUpcomingLeagueDayMatches(target.matches);
    setUpcomingLeagueDayDate(target.leagueDay.date || null);
    setUpcomingLeagueDayVenue(target.leagueDay.venueName ? { venueName: target.leagueDay.venueName, venueAddress: target.leagueDay.venueAddress } : null);
    setUpcomingScores({});
    setIsEditingBet(false);
    setBetSubmitError(null);
    if (isGuest) {
      await fetchUpcomingDivisionRankings(targetDivision, seasonId);
    } else {
      setUpcomingDivisionRankings([]);
    }
    setUpcomingLeagueDayMatchesProgress(75);
    const leagueDayId = upcomingEntryId(target);
    const betInfo = leagueDayId ? await fetchBetInfo(leagueDayId) : null;
    setUpcomingBetInfo(betInfo);
    if (betInfo?.bet) {
      setUpcomingScores(betInfo.bet.predictions);
    }
  };

  const fetchUpcomingLeagueDayMatches = async (homeDivision: string, seasonId: string) => {
    setUpcomingLeagueDayMatchesLoading(true);
    setUpcomingLeagueDayMatchesProgress(0);
    try {
      // No division filter — fetch every division's upcoming day so we can detect guest participation.
      const res = await apiRequest(`/api/public/upcoming-matches`);
      if (!res.ok) return;
      const data = await res.json();
      setUpcomingLeagueDayMatchesProgress(40);
      // Response is { [leagueDayId]: { leagueDay, matches } }
      const entries = Object.values(data) as any[];
      const myId = playerUser?.playerId || '';
      const withMatches = entries.filter((e: any) => e.matches && e.matches.length > 0);
      const homeEntry = withMatches.find((e: any) => e.leagueDay.division === homeDivision);
      // A player can be drawn into more than one other division's day as a guest — keep all of
      // them as dropdown options instead of collapsing to a single auto-picked entry.
      const guestEntries = withMatches
        .filter((e: any) => e.leagueDay.division !== homeDivision && (e.leagueDay.selectedPlayers || []).includes(myId))
        .sort((a: any, b: any) => String(a.leagueDay.date || '').localeCompare(String(b.leagueDay.date || '')));
      const eligible = [homeEntry, ...guestEntries].filter(Boolean);
      setUpcomingEligibleEntries(eligible);

      // Preserve the previous default: a guest day is more actionable than the player's own,
      // so it's preselected when both exist. The dropdown lets them switch from there.
      const defaultEntry = guestEntries[0] || homeEntry;

      if (defaultEntry) {
        await applyUpcomingEntry(defaultEntry, homeDivision, seasonId);
      } else {
        setUpcomingLeagueDayMatches([]);
        setUpcomingBetInfo(null);
        setUpcomingDivisionInfo(null);
        setUpcomingDivisionRankings([]);
        setSelectedUpcomingLeagueDayId('');
      }
      setUpcomingLeagueDayMatchesProgress(100);
    } catch (e) {
      console.error('Error fetching upcoming league day matches', e);
    } finally {
      setUpcomingLeagueDayMatchesLoading(false);
    }
  };

  const selectUpcomingLeagueDay = async (leagueDayId: string) => {
    const entry = upcomingEligibleEntries.find((e: any) => upcomingEntryId(e) === leagueDayId);
    if (!entry || !player?.division || !selectedSeason?.id) return;
    await applyUpcomingEntry(entry, player.division, selectedSeason.id);
  };

  const submitBet = async () => {
    if (!upcomingBetInfo) return;
    setBetSubmitting(true);
    setBetSubmitError(null);
    try {
      const predictions: Record<string, { pair1Games: number; pair2Games: number }> = {};
      upcomingLeagueDayMatches.forEach((match: any) => {
        const s = upcomingScores[match.id];
        predictions[match.id] = { pair1Games: s?.pair1Games ?? 0, pair2Games: s?.pair2Games ?? 0 };
      });
      const res = await apiRequest('/api/bets', {
        method: 'POST',
        body: JSON.stringify({ leagueDayId: upcomingBetInfo.leagueDayId, predictions }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBetSubmitError(data.error || 'Failed to submit bet');
        return;
      }
      setUpcomingBetInfo(prev => prev ? { ...prev, bet: data } : prev);
      setIsEditingBet(false);
    } catch (e) {
      setBetSubmitError('Failed to submit bet');
    } finally {
      setBetSubmitting(false);
    }
  };

  const fetchOpenLeagueDays = async () => {
    try {
      const response = await apiRequest("/api/player/league-days/open", {
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLeagueDays(data);
      }
    } catch (error) {
      console.error("Error fetching league days:", error);
    }
  };

  const fetchUpcomingTournaments = async () => {
    try {
      const params = new URLSearchParams();
      params.append("status", "upcoming,registration");
      if (playerUser?.playerId) {
        params.append("playerId", playerUser.playerId);
      } else {
        params.append("playerView", "true");
      }

      const response = await apiRequest(`/api/tournaments?${params}`);
      if (response.ok) {
        const tournamentsData = await response.json();
        setTournaments(Array.isArray(tournamentsData) ? tournamentsData : []);
      }
    } catch (error) {
      console.error("Error fetching tournaments:", error);
    }
  };

  const fetchReceivedInvites = async () => {
    try {
      const response = await apiRequest("/api/tournament-invitations/received", {
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const pending = Array.isArray(data)
          ? data.filter((inv: any) => inv.status === "pending")
          : [];
        setReceivedInvites(pending);
        setInvitePopupIndex(0);
      }
    } catch (error) {
      console.error("Error fetching received invitations:", error);
    }
  };

  const handleAcceptInvite = async (invitationId: string) => {
    setProcessingInviteId(invitationId);
    try {
      const response = await apiRequest(`/api/tournament-invitations/${invitationId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setInviteError(data.error || "Could not accept the invitation. Please try again.");
        return;
      }
      setInvitePopupIndex((i) => i + 1);
      setTournamentsRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setInviteError("Could not accept the invitation. Please check your connection and try again.");
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleDeclineInvite = async (invitationId: string) => {
    setProcessingInviteId(invitationId);
    try {
      const response = await apiRequest(`/api/tournament-invitations/${invitationId}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setInviteError(data.error || "Could not decline the invitation. Please try again.");
        return;
      }
      setInvitePopupIndex((i) => i + 1);
      setTournamentsRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("Error declining invitation:", error);
      setInviteError("Could not decline the invitation. Please check your connection and try again.");
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleBadgeCelebrationClose = async () => {
    setShowBadgeCelebration(false);

    // Mark badges as seen
    if (playerUser?.playerId) {
      try {
        await apiRequest(
          `/api/players/${playerUser.playerId}/mark-badges-seen`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${playerToken}`,
            },
          },
        );

        // Update local player state to clear unseenBadges
        if (player) {
          setPlayer({ ...player, unseenBadges: [] });
        }
      } catch (error) {
        console.error("Error marking badges as seen:", error);
      }
    }
  };

  const handleLogout = () => {
    playerLogout();
    navigate("/player/login");
  };

  const handleWelcomeRead = async () => {
    setWelcomeDialogHidden(true);
    try {
      const response = await apiRequest("/api/player/me/welcome-read", {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (response.ok) {
        setPlayer((prev) => (prev ? ({ ...prev, welcomeDialogReadAt: new Date().toISOString() } as any) : prev));
      }
    } catch (err) {
      console.error("Failed to save welcome dialog state:", err);
    }
  };

  const handlePrivacyAccept = async () => {
    try {
      const response = await apiRequest("/api/player/accept-privacy-policy", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });

      if (response.ok) {
        setShowPrivacyDialog(false);
        fetchPlayerProfile(); // Refresh to get updated privacy status
      } else {
        throw new Error("Failed to accept privacy policy");
      }
    } catch (error) {
      console.error("Error accepting privacy policy:", error);
      throw error;
    }
  };

  const handlePrivacyDecline = () => {
    // User declined privacy policy, logout
    playerLogout();
    navigate("/player/login");
  };

  const handlePasswordChange = async () => {
    setPasswordChangeError("");

    if (!newPassword || !confirmPassword) {
      setPasswordChangeError("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordChangeError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordChangeError("Passwords do not match");
      return;
    }

    setChangingPassword(true);

    try {
      const response = await apiRequest("/api/player/me/password", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${playerToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword: "", // Not needed for forced password change
          newPassword: newPassword,
        }),
      });

      if (response.ok) {
        // Refresh player data to update requirePasswordChange flag
        await fetchPlayerProfile();
        setNewPassword("");
        setConfirmPassword("");
      } else {
        const data = await response.json();
        setPasswordChangeError(data.error || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordChangeError("Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Helper: extract this player's rounds from a league day draw
  const getMyMatchesFromLeagueDay = (ld: LeagueDay) => {
    if (!ld.rounds || !playerUser?.playerId) return [];
    const result: { roundNumber: number; courtNumber: number; match: any }[] = [];
    ld.rounds.forEach((round: any) => {
      round.matches.forEach((match: any) => {
        const inMatch =
          match.pair1.player1Id === playerUser.playerId ||
          match.pair1.player2Id === playerUser.playerId ||
          match.pair2.player1Id === playerUser.playerId ||
          match.pair2.player2Id === playerUser.playerId;
        if (inMatch) result.push({ roundNumber: round.roundNumber, courtNumber: match.courtNumber, match });
      });
    });
    return result;
  };

  const getRecentFormMatches = () => {
    if (!playerHistory?.matches) return [];

    const leagueDayDateMap = new Map<string, Date>();

    playerHistory.allDivisionLeagueDays?.forEach((day) => {
      if (day.id && day.date) {
        leagueDayDateMap.set(day.id, new Date(day.date));
      }
    });

    playerHistory.leagueDayStats?.forEach((stat) => {
      if (stat.leagueDayId && stat.date && !leagueDayDateMap.has(stat.leagueDayId)) {
        leagueDayDateMap.set(stat.leagueDayId, new Date(stat.date));
      }
    });

    return [...playerHistory.matches]
      .sort((a, b) => {
        const dateA = leagueDayDateMap.get(a.leagueDayId);
        const dateB = leagueDayDateMap.get(b.leagueDayId);

        if (dateA && dateB) {
          const diff = dateB.getTime() - dateA.getTime();
          if (diff === 0) {
            return (b.roundNumber || 0) - (a.roundNumber || 0);
          }
          return diff;
        }

        const fallbackDateA = a.scoredAt || a.createdAt || new Date(0);
        const fallbackDateB = b.scoredAt || b.createdAt || new Date(0);
        const diff = new Date(fallbackDateB).getTime() - new Date(fallbackDateA).getTime();
        if (diff === 0) {
          return (b.roundNumber || 0) - (a.roundNumber || 0);
        }
        return diff;
      })
      .slice(0, 10);
  };

  if (loading) {
    return (
      <>
        <AppBar position="sticky" sx={{ bgcolor: primaryColor }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Dashboard
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  // Division the player was in for the selected season.
  // For past seasons: use the archived seasonStats division.
  // For the active/current season: use the player's current division.
  // Fallback: first key in playerHistory.divisionStats, then player.division.
  const seasonDivision: string =
    (selectedSeason && player?.seasonStats?.[selectedSeason.id]?.division) ||
    player?.division ||
    (playerHistory?.divisionStats ? Object.keys(playerHistory.divisionStats)[0] : "") ||
    "";

  return (
    <>
      {/* <SeasonRegistrationBanner /> */}
      <AppBar position="sticky" sx={{ bgcolor: primaryColor }}>
        <Toolbar>
          {currentOrg?.slug === "padel-eagle" && (
            <Box
              component="img"
              src={padelEagleLogo}
              alt="Padel Eagle"
              sx={{ height: 36, width: 36, mr: 1.5, p: "3px", borderRadius: "8px", background: "#ffffff", objectFit: "contain" }}
            />
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, color: "#ffffff" }}>
            Dashboard
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => navigate("/")}
            sx={{
              mr: { xs: 0.5, sm: 2 },
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <HomeIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            sx={{
              mr: 2,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Public Page
          </Button>
          <IconButton
            color="inherit"
            onClick={() => navigate("/player/profile")}
            sx={{
              mr: { xs: 0.5, sm: 1 },
            }}
            title="Profile & Settings"
          >
            <SettingsIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleLogout}
            sx={{
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <LogoutIcon />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {localStorage.getItem("impersonating") === "true" && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                localStorage.removeItem("impersonating");
                localStorage.removeItem("playerAuthToken");
                localStorage.removeItem("playerAuthUser");
                localStorage.removeItem("playerLoginTime");
                window.close();
              }}
            >
              Exit
            </Button>
          }
          sx={{ borderRadius: 0 }}
        >
          Admin mode: viewing as <strong>{playerUser?.playerName}</strong>. This is a read-only impersonation session.
        </Alert>
      )}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Season Registration Banner */}
        {isSeasonRegistrationOpen && !hasRegistered && (
          <Card sx={{ mb: 3, bgcolor: "primary.light" }}>
            <CardContent
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  🎾{" "}
                  {midSeasonIsOpen
                    ? `Mid-Season Registration is open!`
                    : `Registration for ${seasonName} is open!`}
                </Typography>
                <Typography variant="body2">
                  {midSeasonIsOpen
                    ? `Want to join the rest of ${seasonName}? Secure your spot now.`
                    : `Secure your spot for ${seasonName} now.`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => setRegistrationDialogOpen(true)}
              >
                {midSeasonIsOpen ? "Register for Mid-Season" : "Register Now"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Gear Order Editing Banner */}
        {hasRegistered && gearEditingEnabled && (
          <Card sx={{ mb: 3, bgcolor: "secondary.light" }}>
            <CardContent
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  👕 New T-shirt designs are here!
                </Typography>
                <Typography variant="body2">
                  You can still change your gear order for {seasonName} before it's finalized.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setGearOrderDialogOpen(true)}
              >
                Modify Gear Order
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Announcement Marquee */}
        {announcement && (
          <Box
            sx={{
              bgcolor: announcement?.backgroundColor || "#2e7d32",
              color: "white",
              py: 1.5,
              mb: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-block",
                whiteSpace: "nowrap",
                animation: "marquee 20s linear infinite",
                paddingLeft: "100%",
                "@keyframes marquee": {
                  "0%": { transform: "translateX(0)" },
                  "100%": { transform: "translateX(-100%)" },
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                  display: "inline-block",
                }}
              >
                📢 {announcement.message}
              </Typography>
            </Box>
          </Box>
        )}

        {playerUser?.requirePasswordChange && (
          <Card sx={{ mb: 3, bgcolor: "warning.light" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⚠️ Password Change Required
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You are using a temporary password. Please change it in your
                profile.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate("/player/profile")}
              >
                Change Password Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Prominent Next Registration Timer - Only for non-guest, non-tournament-only and active players */}
        {!player?.isGuest &&
          !player?.isTournamentOnly &&
          currentOrg?.competitionType === "both" &&
          dashboardView === "league" &&
          (() => {
            const nextScheduled = leagueDays
              .filter(
                (ld) =>
                  !ld.registrationOpen &&
                  ld.registrationScheduledFor &&
                  new Date(ld.registrationScheduledFor) > new Date(),
              )
              .sort(
                (a, b) =>
                  new Date(a.registrationScheduledFor!).getTime() -
                  new Date(b.registrationScheduledFor!).getTime(),
              )[0];

            if (nextScheduled) {
              return (
                <Card
                  elevation={3}
                  sx={{
                    mb: 3,
                    background:
                      player?.isActive === false
                        ? "linear-gradient(135deg, #9e9e9e 0%, #616161 100%)"
                        : brandGradient,
                    color: "white",
                    cursor: player?.isActive !== false ? "pointer" : "not-allowed",
                    opacity: player?.isActive === false ? 0.7 : 1,
                  }}
                  onClick={() => {
                    if (player?.isActive !== false) {
                      navigate("/player/league-days");
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {player?.isActive === false && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          You haven't registered for the {seasonName.split(' ')[0].toLowerCase()} season
                        </Typography>
                        <Typography variant="caption">
                          Contact an admin to register for the new season
                        </Typography>
                      </Alert>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", textAlign: "center" }}
                      >
                        🔔 Upcoming Registration
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", opacity: 0.9 }}
                      >
                        League Day on{" "}
                        {formatLeagueDayDateCustom(
                          nextScheduled.date,
                          {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                          },
                          "en-US"
                        )}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          opacity: 0.95,
                        }}
                      >
                        Opens on:{" "}
                        {new Date(
                          nextScheduled.registrationScheduledFor!,
                        ).toLocaleString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </Typography>
                      <Box sx={{ width: "100%", maxWidth: "600px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            borderRadius: 1,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <ClockIcon sx={{ color: "white" }} />
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "medium", color: "white" }}
                          >
                            Registration opens in:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              fontFamily: "monospace",
                            }}
                          >
                            {(() => {
                              const difference =
                                new Date(
                                  nextScheduled.registrationScheduledFor!,
                                ).getTime() - new Date().getTime();
                              const days = Math.floor(
                                difference / (1000 * 60 * 60 * 24),
                              );
                              const hours = Math.floor(
                                (difference / (1000 * 60 * 60)) % 24,
                              );
                              const minutes = Math.floor(
                                (difference / 1000 / 60) % 60,
                              );
                              const seconds = Math.floor(
                                (difference / 1000) % 60,
                              );
                              return (
                                <>
                                  {days > 0 && (
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        color: "white",
                                      }}
                                    >
                                      {days}d
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", color: "white" }}
                                  >
                                    {hours}h
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", color: "white" }}
                                  >
                                    {minutes}m
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", color: "white" }}
                                  >
                                    {seconds}s
                                  </Typography>
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, textAlign: "center" }}
                      >
                        Click to view all league days
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            }
            return null;
          })()}

        {/* Tournament Registration Timer - Show when in tournament view */}
        {currentOrg?.competitionType === "both" &&
          effectiveDashboardView === "tournament" &&
          (() => {
            const nextTournament = tournaments
              .filter((t) => {
                const regCloseDate = new Date(t.registrationCloseDate);
                const now = new Date();
                return t.status === "upcoming" && regCloseDate > now;
              })
              .sort(
                (a, b) =>
                  new Date(a.registrationOpenDate).getTime() -
                  new Date(b.registrationOpenDate).getTime(),
              )[0];

            if (nextTournament) {
              const regOpenDate = new Date(nextTournament.registrationOpenDate);
              const now = new Date();
              const isRegistrationOpen =
                regOpenDate <= now && nextTournament.status === "registration";
              const showTimer = regOpenDate > now;

              return (
                <Card
                  elevation={3}
                  sx={{
                    mb: 3,
                    background:
                      brandGradient,
                    color: "white",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/view-tournaments")}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", textAlign: "center" }}
                      >
                        🏆{" "}
                        {isRegistrationOpen
                          ? "Registration Open!"
                          : "Upcoming Tournament"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", opacity: 0.9 }}
                      >
                        {nextTournament.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", opacity: 0.9 }}
                      >
                        {nextTournament.level} •{" "}
                        {new Date(nextTournament.startDate).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                          },
                        )}
                      </Typography>
                      {isRegistrationOpen && (
                        <Typography
                          variant="body1"
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            opacity: 0.95,
                          }}
                        >
                          Closes on:{" "}
                          {new Date(
                            nextTournament.registrationCloseDate,
                          ).toLocaleString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </Typography>
                      )}
                      {showTimer && (
                        <>
                          <Typography
                            variant="body1"
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              opacity: 0.95,
                            }}
                          >
                            Registration opens:{" "}
                            {regOpenDate.toLocaleString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </Typography>
                          <Box sx={{ width: "100%", maxWidth: "600px" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 2,
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                borderRadius: 1,
                                justifyContent: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <ClockIcon sx={{ color: "white" }} />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "medium", color: "white" }}
                              >
                                Opens in:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  fontFamily: "monospace",
                                }}
                              >
                                {(() => {
                                  const difference =
                                    regOpenDate.getTime() - now.getTime();
                                  const days = Math.floor(
                                    difference / (1000 * 60 * 60 * 24),
                                  );
                                  const hours = Math.floor(
                                    (difference / (1000 * 60 * 60)) % 24,
                                  );
                                  const minutes = Math.floor(
                                    (difference / 1000 / 60) % 60,
                                  );
                                  const seconds = Math.floor(
                                    (difference / 1000) % 60,
                                  );
                                  return (
                                    <>
                                      {days > 0 && (
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "white",
                                          }}
                                        >
                                          {days}d
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "white",
                                        }}
                                      >
                                        {hours}h
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "white",
                                        }}
                                      >
                                        {minutes}m
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "white",
                                        }}
                                      >
                                        {seconds}s
                                      </Typography>
                                    </>
                                  );
                                })()}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, textAlign: "center" }}
                      >
                        Click to view tournament details
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            }
            return null;
          })()}

        <Grid container spacing={3}>
          {/* Player Header Card - Always shown */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Avatar
                    src={player?.profilePicture}
                    sx={{
                      width: { xs: 60, md: 80 },
                      height: { xs: 60, md: 80 },
                    }}
                  >
                    {player?.name?.charAt(0) || "P"}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: primaryColor }}
                    >
                      {player?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {player?.division}
                    </Typography>
                    {/* Badges Section */}
                    {player?.badges && player.badges.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <BadgeDisplay badges={player.badges} size="small" />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {/* Edit Profile & Change Password */}
                  <Button
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate("/player/profile")}
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      textTransform: "none",
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    Edit Profile & Change Password
                  </Button>

                  {/* Banned Status Warning - GDPR Compliant */}
                  {player?.isBanned && (
                    <Alert severity="error" sx={{ width: "100%", mt: 2 }}>
                      <strong>Account Restricted</strong>
                      <Box sx={{ mt: 1 }}>
                        Your account has been temporarily restricted from registering for events.
                        {player.bannedReason && (
                          <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            Reason: {player.bannedReason}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ mt: 2, fontWeight: 'bold' }}>
                        You can still:
                      </Box>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        <li>View your stats and match history</li>
                        <li>Request account deletion (GDPR)</li>
                        <li>Download your personal data (GDPR)</li>
                      </ul>
                      <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                        For assistance, please contact an administrator.
                      </Box>
                    </Alert>
                  )}

                  {/* Register for League Days - Only for league organizations, non-guest players, and non-banned */}
                  {isLeagueOrg && !player?.isGuest && !player?.isBanned && !player?.isTournamentOnly && (
                    <Tooltip 
                      title={player?.isActive === false ? `You haven't registered for the ${seasonName.split(' ')[0].toLowerCase()} season` : ""}
                      arrow
                    >
                      <span style={{ width: "100%" }}>
                        <Button
                          variant="contained"
                          startIcon={<EventIcon />}
                          onClick={() => navigate("/player/league-days")}
                          disabled={player?.isActive === false}
                          sx={{
                            width: "100%",
                            borderRadius: 3,
                            py: 1.5,
                            px: 3,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            boxShadow: 2,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: 4,
                            },
                            "&.Mui-disabled": {
                              background: "#ccc",
                              color: "rgba(0, 0, 0, 0.5)",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          Register for League Days
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {/* Register for Tournaments - Only for tournament organizations and non-banned */}
                  {(currentOrg?.competitionType === "tournament" ||
                    currentOrg?.competitionType === "both") && !player?.isBanned && (
                    <Button
                      variant="contained"
                      startIcon={<EventIcon />}
                      onClick={() => navigate("/view-tournaments")}
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        px: 3,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        background: brandGradient,
                        boxShadow: 2,
                        "&:hover": {
                          background: brandGradient,
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                          opacity: 0.95,
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Register for Tournaments
                    </Button>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Dashboard View Toggle - Only show for organizations with both leagues and tournaments, and not for tournament-only accounts (they have no league view to switch to) */}
          {currentOrg?.competitionType === "both" &&
            !player?.isTournamentOnly && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: primaryColor }}
                  >
                    View Dashboard
                  </Typography>
                  <ToggleButtonGroup
                    value={dashboardView}
                    exclusive
                    onChange={(e, newView) => {
                      if (newView !== null) {
                        setDashboardView(newView);
                      }
                    }}
                    aria-label="dashboard view"
                    fullWidth
                    sx={{
                      maxWidth: 500,
                      "& .MuiToggleButton-root": {
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                      },
                    }}
                  >
                    <ToggleButton value="league" aria-label="league view">
                      <LeaderboardIcon sx={{ mr: 1 }} />
                      League
                    </ToggleButton>
                    <ToggleButton
                      value="tournament"
                      aria-label="tournament view"
                    >
                      <TrophyIcon sx={{ mr: 1 }} />
                      Tournaments
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* LEAGUE VIEW CONTENT - Show when in league view or not a "both" org */}
          {(currentOrg?.competitionType !== "both" ||
            effectiveDashboardView === "league") && (
            <>
              {/* Season Selector - top of league section */}
              {allSeasons && allSeasons.length > 1 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", minWidth: 80 }}>
                          Season:
                        </Typography>
                        <Box sx={{ minWidth: 220 }}>
                          <SeasonSelector fullWidth excludeDrafts size="small" />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Welcome card — shown only for brand-new players who have never played */}
              {currentOrg?.slug !== "padel-eagle" &&
                player &&
                (player.gamesPlayed ?? 0) === 0 &&
                (!player.seasonStats || Object.keys(player.seasonStats).length === 0) && (
                <Grid item xs={12}>
                  <Card sx={{
                    background: brandGradient,
                    color: "white",
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                        👋 Welcome to your Dashboard!
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2, opacity: 0.95 }}>
                        Here's everything you need to know to get started.
                      </Typography>

                      {/* Stats section */}
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, opacity: 0.9, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.75rem" }}>
                        📈 What you'll see once you start playing
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 2.5, "& li": { mb: 0.75 } }}>
                        <li><Typography variant="body2">📊 <strong>Performance Insights</strong> — attendance rate, win rate and points trend over the season.</Typography></li>
                        <li><Typography variant="body2">🏆 <strong>Points Breakdown</strong> — how your ranking points are calculated game by game.</Typography></li>
                        <li><Typography variant="body2">📅 <strong>Match History</strong> — every match you've played, with partners, opponents and scores.</Typography></li>
                        <li><Typography variant="body2">🏅 <strong>Badges</strong> — achievements for milestones like first win, consecutive attendance, and more.</Typography></li>
                      </Box>

                      <Divider sx={{ borderColor: "rgba(255,255,255,0.3)", mb: 2 }} />

                      {/* Navigation section */}
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, opacity: 0.9, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.75rem" }}>
                        🧭 Getting around
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 2.5, "& li": { mb: 0.75 } }}>
                        <li><Typography variant="body2">⚙️ <strong>Gear icon (top right)</strong> — opens your profile settings: edit your name, photo, email and privacy preferences.</Typography></li>
                        <li><Typography variant="body2">🌐 <strong>Public Page button (top right)</strong> — view the public rankings, results and records visible to everyone.</Typography></li>
                        <li><Typography variant="body2">🔑 <strong>Change Password</strong> — available inside your profile settings (gear icon). Use this to set a personal password.</Typography></li>
                      </Box>

                      <Divider sx={{ borderColor: "rgba(255,255,255,0.3)", mb: 2 }} />

                      {/* Actions section */}
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, opacity: 0.9, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.75rem" }}>
                        🎮 Actions
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 2, "& li": { mb: 0.75 } }}>
                        <li><Typography variant="body2">📋 <strong>Register for League Days</strong> — you can register yourself for upcoming league days here.</Typography></li>
                        <li><Typography variant="body2">🏸 <strong>Register for Tournaments</strong> — if there are available tournaments, you can register yourself for those tournaments here.</Typography></li>
                      </Box>

                      <Typography variant="body2" sx={{ opacity: 0.85, fontStyle: "italic" }}>
                        Your upcoming match schedule for the next league day is shown below. Good luck! 🎾
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Upcoming Matches — drawn rounds for any active league day */}
              {(() => {
                const daysWithDraw = leagueDays.filter(ld => {
                  const myMatches = getMyMatchesFromLeagueDay(ld);
                  return myMatches.length > 0 && !ld.isCompleted;
                });
                if (daysWithDraw.length === 0) return null;
                return daysWithDraw.map(ld => {
                  const myMatches = getMyMatchesFromLeagueDay(ld);
                  // Build a playerId → name lookup from playerAssignments
                  const playerNameLookup: Record<string, string> = {};
                  ((ld as any).playerAssignments || []).forEach((a: any) => {
                    if (a.playerId && a.playerName) playerNameLookup[a.playerId] = a.playerName;
                  });
                  const resolvePlayerName = (id?: string) => id ? (playerNameLookup[id] || "Unknown") : "TBD";
                  const ldDate = ld.date ? new Date(ld.date).toLocaleDateString("en-US", {
                    weekday: "long", month: "long", day: "numeric"
                  }) : "Upcoming";
                  return (
                    <Grid item xs={12} key={ld.id}>
                      <Card elevation={3} sx={{ border: `2px solid ${primaryColor}` }}>
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                              🎾 Your Matches — {ld.name || ldDate}
                            </Typography>
                            <Chip label={ldDate} size="small" color="primary" variant="outlined" />
                          </Box>
                          <VenueBadge
                            venueName={ld.venueName}
                            venueAddress={ld.venueAddress}
                            variant="body2"
                          />
                          <Grid container spacing={2}>
                            {myMatches.map(({ roundNumber, courtNumber, match }) => {
                              const isP1Side =
                                match.pair1.player1Id === playerUser?.playerId ||
                                match.pair1.player2Id === playerUser?.playerId;
                              const partnerName = isP1Side
                                ? match.pair1.player1Id === playerUser?.playerId
                                  ? resolvePlayerName(match.pair1.player2Id)
                                  : resolvePlayerName(match.pair1.player1Id)
                                : match.pair2.player1Id === playerUser?.playerId
                                  ? resolvePlayerName(match.pair2.player2Id)
                                  : resolvePlayerName(match.pair2.player1Id);
                              const opp1 = isP1Side ? resolvePlayerName(match.pair2.player1Id) : resolvePlayerName(match.pair1.player1Id);
                              const opp2 = isP1Side ? resolvePlayerName(match.pair2.player2Id) : resolvePlayerName(match.pair1.player2Id);
                              return (
                                <Grid item xs={12} sm={6} md={4} key={`${roundNumber}-${courtNumber}`}>
                                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                                      <Chip label={`Round ${roundNumber}`} color="primary" size="small" />
                                      <Chip label={`Court ${courtNumber}`} variant="outlined" size="small" />
                                    </Box>
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography variant="body1" sx={{ fontWeight: "bold", color: "primary.main" }}>
                                        You & {partnerName}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ my: 0.5 }}>vs</Typography>
                                      <Typography variant="body1">{opp1} & {opp2}</Typography>
                                    </Box>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                          <Box sx={{ mt: 2, textAlign: "right" }}>
                            <Button size="small" variant="outlined" onClick={() => navigate("/player/league-days")}>
                              View Full Schedule
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                });
              })()}

              {/* Badge Points This Season */}
              {(() => {
                if (!selectedSeason?.id || !player?.badges) return null;
                // Only count badges for the current division (mirrors how the ranking computes totalPoints).
                // Badges without a division field default to the player's registered division.
                const divisionBadges = player.badges.filter(
                  (b: any) =>
                    b.seasonId === selectedSeason.id &&
                    (b.pointsAwarded ?? 0) > 0 &&
                    (b.division ? b.division === seasonDivision : true)
                );
                const milestoneBadges = player.badges.filter(
                  (b: any) =>
                    ["all_season_warrior", "division_champion", "sp_team"].includes(b.type) &&
                    (b.pointsAwarded ?? 0) > 0
                );
                const allPointBadges = [...divisionBadges, ...milestoneBadges];
                // Cross-division badges (earned in other divisions this season)
                const crossDivBadges = player.badges.filter(
                  (b: any) =>
                    b.seasonId === selectedSeason.id &&
                    (b.pointsAwarded ?? 0) > 0 &&
                    b.division && b.division !== seasonDivision
                );
                if (allPointBadges.length === 0 && crossDivBadges.length === 0) return null;
                const totalBadgePoints = allPointBadges.reduce(
                  (sum: number, b: any) => sum + (b.pointsAwarded ?? 0),
                  0
                );
                return (
                  <Grid item xs={12}>
                    <Card elevation={3} sx={{ border: `2px solid`, borderColor: "warning.main" }}>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            🏅 Badge Points — {selectedSeason.name}
                          </Typography>
                          <Chip
                            label={`+${totalBadgePoints} pts from badges`}
                            color="warning"
                            sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          {allPointBadges.map((b: any, idx: number) => (
                            <Paper
                              key={`${b.type}-${idx}`}
                              elevation={1}
                              sx={{
                                px: 2,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography sx={{ fontSize: 20 }}>
                                {({
                                  first_win: "🎉", giant_killer: "⚔️", win_streak_10: "🔥",
                                  close_call: "💪", perfect_day: "⭐", veteran: "🏅",
                                  partner_carry: "🎯", clean_sweep: "🧹", mr_ms_consistent: "🎲",
                                  century: "💯", iron_man: "🔥", all_season_warrior: "⚔️",
                                  division_hopper: "🔀", unlucky: "😅", division_champion: "👑",
                                  sp_team: "🏆",
                                } as Record<string, string>)[b.type] ?? "🏅"}
                              </Typography>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                  {b.type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  +{b.pointsAwarded} pts
                                </Typography>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                        {crossDivBadges.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                            +{crossDivBadges.reduce((s: number, b: any) => s + (b.pointsAwarded ?? 0), 0)} pts from badges earned in other divisions ({crossDivBadges.map((b: any) => b.division).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i).join(", ")}) — these count toward those divisions' rankings, not {seasonDivision}.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}

              {/* Performance Insights Card */}
              {playerHistory?.divisionStats &&
                seasonDivision &&
                playerHistory.divisionStats[seasonDivision] && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 2 }}
                        >
                          📊 Performance Insights - {selectedSeason?.name || seasonDivision}
                        </Typography>

                        <Grid container spacing={2}>
                          {/* Attendance Rate */}
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: "center",
                                bgcolor: "background.default",
                                border: "2px solid",
                                borderColor: (() => {
                                  const attended = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft && ld.participated
                                  ).length || 0;
                                  const total = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft
                                  ).length || 1;
                                  const rate = attended / total;
                                  return rate >= 0.8 ? "success.main" : rate >= 0.5 ? "warning.main" : "error.main";
                                })(),
                              }}
                            >
                              <Typography
                                variant="h3"
                                sx={{
                                  fontWeight: "bold",
                                  color: (() => {
                                    const attended = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                      ld.isCompleted && !ld.isDraft && ld.participated
                                    ).length || 0;
                                    const total = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                      ld.isCompleted && !ld.isDraft
                                    ).length || 1;
                                    const rate = attended / total;
                                    return rate >= 0.8 ? "success.main" : rate >= 0.5 ? "warning.main" : "error.main";
                                  })(),
                                }}
                              >
                                {(() => {
                                  const attended = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft && ld.participated
                                  ).length || 0;
                                  const total = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft
                                  ).length || 1;
                                  return Math.round((attended / total) * 100);
                                })()}%
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Attendance Rate
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {playerHistory.allDivisionLeagueDays?.filter(ld => 
                                  ld.isCompleted && !ld.isDraft && ld.participated
                                ).length || 0}/
                                {playerHistory.allDivisionLeagueDays?.filter(ld => 
                                  ld.isCompleted && !ld.isDraft
                                ).length || 0} days
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Average Points Per League Day */}
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: "center",
                                bgcolor: "background.default",
                                border: "2px solid",
                                borderColor: "primary.main",
                              }}
                            >
                              <Typography
                                variant="h3"
                                sx={{
                                  fontWeight: "bold",
                                  color: "primary.main",
                                }}
                              >
                                {(() => {
                                  const totalDaysPlayed = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft && ld.participated
                                  ).length || 0;
                                  
                                  if (totalDaysPlayed === 0) return "0.0";
                                  
                                  // Sum up points from leagueDayStats (excludes bonuses/penalties)
                                  const totalLeagueDayPoints = playerHistory.leagueDayStats
                                    ?.filter(stat => stat.participated !== false)
                                    .reduce((sum, stat) => sum + (stat.points || 0), 0) || 0;
                                  
                                  return (totalLeagueDayPoints / totalDaysPlayed).toFixed(1);
                                })()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Avg Points/Day
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Excludes bonuses/penalties
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Current Form (Last 5 Days) */}
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: "center",
                                bgcolor: "background.default",
                                border: "2px solid",
                                borderColor: (() => {
                                  const recentDays = [...(playerHistory.leagueDayStats || [])]
                                    .filter(stat => stat.participated !== false)
                                    .sort((a, b) => {
                                      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                                      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                                      return dateB.getTime() - dateA.getTime();
                                    })
                                    .slice(0, 5);
                                  
                                  if (recentDays.length === 0) return "grey.400";
                                  
                                  // Calculate overall average from leagueDayStats (same as Avg Points/Day)
                                  const totalDaysPlayed = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft && ld.participated
                                  ).length || 0;
                                  const totalLeagueDayPoints = playerHistory.leagueDayStats
                                    ?.filter(stat => stat.participated !== false)
                                    .reduce((sum, stat) => sum + (stat.points || 0), 0) || 0;
                                  const overallAvg = totalDaysPlayed > 0 ? totalLeagueDayPoints / totalDaysPlayed : 0;
                                  
                                  const avgRecent = recentDays.reduce((sum, d) => sum + (d.points || 0), 0) / recentDays.length;
                                  const difference = avgRecent - overallAvg;
                                  
                                  if (difference >= 1) return "success.main";
                                  if (difference >= -1) return "info.main";
                                  return "error.main";
                                })(),
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
                                <Typography
                                  variant="h3"
                                  sx={{
                                    fontWeight: "bold",
                                    color: (() => {
                                      const recentDays = [...(playerHistory.leagueDayStats || [])]
                                        .filter(stat => stat.participated !== false)
                                        .sort((a, b) => {
                                          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                                          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                                          return dateB.getTime() - dateA.getTime();
                                        })
                                        .slice(0, 5);
                                      
                                      if (recentDays.length === 0) return "grey.600";
                                      
                                      // Calculate overall average from leagueDayStats (same as Avg Points/Day)
                                      const totalDaysPlayed = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                        ld.isCompleted && !ld.isDraft && ld.participated
                                      ).length || 0;
                                      const totalLeagueDayPoints = playerHistory.leagueDayStats
                                        ?.filter(stat => stat.participated !== false)
                                        .reduce((sum, stat) => sum + (stat.points || 0), 0) || 0;
                                      const overallAvg = totalDaysPlayed > 0 ? totalLeagueDayPoints / totalDaysPlayed : 0;
                                      
                                      const avgRecent = recentDays.reduce((sum, d) => sum + (d.points || 0), 0) / recentDays.length;
                                      const difference = avgRecent - overallAvg;
                                      
                                      if (difference >= 1) return "success.main";
                                      if (difference >= -1) return "info.main";
                                      return "error.main";
                                    })(),
                                  }}
                                >
                                  {(() => {
                                    const recentDays = [...(playerHistory.leagueDayStats || [])]
                                      .filter(stat => stat.participated !== false)
                                      .sort((a, b) => {
                                        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                                        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                                        return dateB.getTime() - dateA.getTime();
                                      })
                                      .slice(0, 5);
                                    
                                    if (recentDays.length === 0) return "--";
                                    
                                    // Calculate overall average from leagueDayStats (same as Avg Points/Day)
                                    const totalDaysPlayed = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                      ld.isCompleted && !ld.isDraft && ld.participated
                                    ).length || 0;
                                    const totalLeagueDayPoints = playerHistory.leagueDayStats
                                      ?.filter(stat => stat.participated !== false)
                                      .reduce((sum, stat) => sum + (stat.points || 0), 0) || 0;
                                    const overallAvg = totalDaysPlayed > 0 ? totalLeagueDayPoints / totalDaysPlayed : 0;
                                    
                                    const avgRecent = recentDays.reduce((sum, d) => sum + (d.points || 0), 0) / recentDays.length;
                                    const difference = avgRecent - overallAvg;
                                    
                                    if (difference >= 1) return "🔥";
                                    if (difference >= -1) return "📈";
                                    return "📉";
                                  })()}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Current Form
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(() => {
                                  const recentDays = [...(playerHistory.leagueDayStats || [])]
                                    .filter(stat => stat.participated !== false)
                                    .sort((a, b) => {
                                      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                                      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                                      return dateB.getTime() - dateA.getTime();
                                    })
                                    .slice(0, 5);
                                  
                                  if (recentDays.length === 0) return "No recent data";
                                  
                                  // Calculate overall average from leagueDayStats (same as Avg Points/Day)
                                  const totalDaysPlayed = playerHistory.allDivisionLeagueDays?.filter(ld => 
                                    ld.isCompleted && !ld.isDraft && ld.participated
                                  ).length || 0;
                                  const totalLeagueDayPoints = playerHistory.leagueDayStats
                                    ?.filter(stat => stat.participated !== false)
                                    .reduce((sum, stat) => sum + (stat.points || 0), 0) || 0;
                                  const overallAvg = totalDaysPlayed > 0 ? totalLeagueDayPoints / totalDaysPlayed : 0;
                                  
                                  const avgRecent = recentDays.reduce((sum, d) => sum + (d.points || 0), 0) / recentDays.length;
                                  const difference = avgRecent - overallAvg;
                                  
                                  if (difference >= 1) return "Hot streak!";
                                  if (difference >= -1) return "Steady form";
                                  return "Below average";
                                })()}
                              </Typography>
                            </Paper>
                          </Grid>

                          {/* Recent Match Form (Last 10 Matches) */}
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: "center",
                                bgcolor: "background.default",
                                border: "2px solid",
                                borderColor: "info.main",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1, fontWeight: "bold" }}
                              >
                                Recent Match Form
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                  flexWrap: "wrap",
                                  mb: 1,
                                }}
                              >
                                {(() => {
                                  // Keep this ordering identical to Match History.
                                  const recentMatches = getRecentFormMatches();

                                  if (recentMatches.length === 0) {
                                    return (
                                      <Typography variant="caption" color="text.secondary">
                                        No matches yet
                                      </Typography>
                                    );
                                  }

                                  return recentMatches.map((match, index) => {
                                    const isPair1 =
                                      match.pair1.player1Id === playerUser?.playerId ||
                                      match.pair1.player2Id === playerUser?.playerId;
                                    const didWin = isPair1
                                      ? match.score.winnerPair === 1
                                      : match.score.winnerPair === 2;

                                    return (
                                      <Tooltip
                                        key={index}
                                        title={`${didWin ? "Won" : "Lost"} ${match.score.pair1Games}-${match.score.pair2Games}`}
                                      >
                                        <Box
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: didWin ? "success.main" : "error.main",
                                            color: "white",
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {didWin ? "W" : "L"}
                                        </Box>
                                      </Tooltip>
                                    );
                                  });
                                })()}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {(() => {
                                  const recentMatches = getRecentFormMatches();
                                  
                                  const wins = recentMatches.filter(match => {
                                    const isPair1 =
                                      match.pair1.player1Id === playerUser?.playerId ||
                                      match.pair1.player2Id === playerUser?.playerId;
                                    return isPair1
                                      ? match.score.winnerPair === 1
                                      : match.score.winnerPair === 2;
                                  }).length;

                                  return `${wins}W-${recentMatches.length - wins}L (Last ${recentMatches.length})`;
                                })()}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

              {/* Points Breakdown Card */}
              {playerHistory?.divisionStats &&
                seasonDivision &&
                playerHistory.divisionStats[seasonDivision] && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 2 }}
                        >
                          🎯 Points Breakdown - {selectedSeason?.name || seasonDivision}
                        </Typography>

                        <Box>
                          <Paper
                            sx={{ p: 2, mb: 2, bgcolor: "background.default" }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "primary.main",
                                    }}
                                  >
                                    {playerHistory.divisionStats[
                                      seasonDivision
                                    ].gamesWon || 0}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Games Won
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "success.main",
                                    }}
                                  >
                                    +
                                    {playerHistory.divisionStats[
                                      seasonDivision
                                    ].gamesWon || 0}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Base Points
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "bold",
                                      color:
                                        (playerHistory.divisionStats[
                                          seasonDivision
                                        ].totalPoints || 0) -
                                          (playerHistory.divisionStats[
                                            seasonDivision
                                          ].gamesWon || 0) >=
                                        0
                                          ? "success.main"
                                          : "error.main",
                                    }}
                                  >
                                    {(playerHistory.divisionStats[
                                      seasonDivision
                                    ].totalPoints || 0) -
                                      (playerHistory.divisionStats[
                                        seasonDivision
                                      ].gamesWon || 0) >
                                    0
                                      ? "+"
                                      : ""}
                                    {(playerHistory.divisionStats[
                                      seasonDivision
                                    ].totalPoints || 0) -
                                      (playerHistory.divisionStats[
                                        seasonDivision
                                      ].gamesWon || 0)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Penalties/Bonuses
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Box
                                  sx={{
                                    textAlign: "center",
                                    p: 1,
                                    bgcolor: "primary.light",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "primary.contrastText",
                                    }}
                                  >
                                    {Math.max(0,
                                      (playerHistory.divisionStats[seasonDivision].gamesWon || 0) +
                                      (player?.badges || []).filter((b: any) =>
                                        b.seasonId === selectedSeason?.id &&
                                        (b.pointsAwarded ?? 0) > 0 &&
                                        (b.division ? b.division === seasonDivision : true)
                                      ).reduce((s: number, b: any) => s + (b.pointsAwarded ?? 0), 0) -
                                      penaltiesAndBonuses
                                        .filter((pb) => pb.type === 'penalty')
                                        .reduce((s, pb) => s + pb.points, 0) +
                                      penaltiesAndBonuses
                                        .filter((pb) => pb.type === 'bonus')
                                        .reduce((s, pb) => s + pb.points, 0)
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "primary.contrastText" }}
                                  >
                                    Total Points
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>

                          {penaltiesAndBonuses.length > 0 && (
                            <Box>
                              <Divider sx={{ my: 2 }} />
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", mb: 2 }}
                              >
                                📋 Penalties & Bonuses History
                              </Typography>
                              {penaltiesAndBonuses.map((pb) => (
                                <Paper
                                  key={pb.id}
                                  sx={{
                                    p: 2,
                                    mb: 1.5,
                                    border: "1px solid",
                                    borderColor:
                                      pb.type === "bonus"
                                        ? "success.main"
                                        : "error.main",
                                    bgcolor:
                                      pb.type === "bonus"
                                        ? "success.light"
                                        : "error.light",
                                    opacity: 0.9,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 0.5,
                                        }}
                                      >
                                        <Chip
                                          label={
                                            pb.type === "bonus"
                                              ? "Bonus"
                                              : "Penalty"
                                          }
                                          color={
                                            pb.type === "bonus"
                                              ? "success"
                                              : "error"
                                          }
                                          size="small"
                                        />
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color:
                                              pb.type === "bonus"
                                                ? "success.dark"
                                                : "error.dark",
                                          }}
                                        >
                                          {pb.points > 0 ? "+" : ""}
                                          {pb.points} points
                                        </Typography>
                                      </Box>
                                      {pb.reason && (
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 1 }}
                                        >
                                          <strong>Reason:</strong> {pb.reason}
                                        </Typography>
                                      )}
                                      {pb.disqualificationReason && (
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                          <Typography variant="body2">
                                            <strong>Disqualification:</strong>{" "}
                                            {pb.disqualificationReason}
                                          </Typography>
                                        </Alert>
                                      )}
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {new Date(
                                          pb.createdAt,
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                        })}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                      >
                                        {new Date(
                                          pb.createdAt,
                                        ).toLocaleTimeString("en-US", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          )}

                          {penaltiesAndBonuses.length === 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              No penalties or bonuses have been applied to your
                              account this season.
                            </Alert>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

              {/* Ranking Insights Card — active season + has played + rankings loaded */}
              {selectedSeason?.id === activeSeason?.id &&
                divisionRankings.length > 0 &&
                (player?.gamesPlayed ?? 0) > 0 && (() => {
                  const myId = playerUser?.playerId || '';
                  // When the selected results day was a guest appearance in another division,
                  // simulate against that division's rankings instead of the player's own.
                  const resultsRankingsSource = resultsDivisionInfo?.isGuest ? resultsDivisionRankings : divisionRankings;
                  const rankingPool = showGuestPlayers
                    ? resultsRankingsSource
                    : resultsRankingsSource.filter(p => !p.isGuestPlayer && !p.isGuest);
                  const currentRanked = rankingPool.filter(p => p.gamesPlayed > 0);
                  const myCurrentPos = currentRanked.findIndex(p => p.playerId === myId);
                  const me = currentRanked[myCurrentPos];
                  const playersAbove = myCurrentPos > 0 ? currentRanked.slice(0, myCurrentPos) : [];

                  // Build deltas from score overrides — tracks actual individual game count changes
                  const matchDeltas: Record<string, GameDelta> = {};
                  let changedCount = 0;
                  lastLeagueDayMatches.forEach((match: any) => {
                    const override = scoreOverrides[match.id];
                    if (!override) return;
                    const origP1g: number = match.score?.pair1Games ?? 0;
                    const origP2g: number = match.score?.pair2Games ?? 0;
                    const newP1g = override.pair1Games;
                    const newP2g = override.pair2Games;
                    if (newP1g === origP1g && newP2g === origP2g) return; // nothing changed
                    changedCount++;
                    const pair1Ids: string[] = [match.pair1.player1Id, match.pair1.player2Id].filter(Boolean);
                    const pair2Ids: string[] = [match.pair2.player1Id, match.pair2.player2Id].filter(Boolean);
                    // pair1: gamesWon changes by (newP1g - origP1g), gamesLost by (newP2g - origP2g)
                    pair1Ids.forEach((pid: string) => {
                      if (!matchDeltas[pid]) matchDeltas[pid] = { gamesWonDelta: 0, gamesLostDelta: 0 };
                      matchDeltas[pid].gamesWonDelta += newP1g - origP1g;
                      matchDeltas[pid].gamesLostDelta += newP2g - origP2g;
                    });
                    // pair2: gamesWon changes by (newP2g - origP2g), gamesLost by (newP1g - origP1g)
                    pair2Ids.forEach((pid: string) => {
                      if (!matchDeltas[pid]) matchDeltas[pid] = { gamesWonDelta: 0, gamesLostDelta: 0 };
                      matchDeltas[pid].gamesWonDelta += newP2g - origP2g;
                      matchDeltas[pid].gamesLostDelta += newP1g - origP1g;
                    });
                  });

                  const hasChanges = changedCount > 0;
                  const simRanked = hasChanges
                    ? recomputeHybridRankings(rankingPool, matchDeltas).filter(p => p.gamesPlayed > 0)
                    : currentRanked;
                  const mySimPos = simRanked.findIndex(p => p.playerId === myId);
                  const mySimScore = hasChanges ? (simRanked[mySimPos]?.simScore ?? 0) : 0;
                  const myCurrentScore = me ? ((me as any).hybridScore ?? 0) : 0;

                  return (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <TrendingUpIcon sx={{ color: secondaryColor }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              📈 Ranking Insights
                            </Typography>
                            <Chip label={player?.division} size="small" variant="outlined" />
                            <FormControlLabel
                              sx={{ ml: 'auto', mr: 0 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={showGuestPlayers}
                                  onChange={(e) => setShowGuestPlayers(e.target.checked)}
                                />
                              }
                              label={<Typography variant="caption" color="text.secondary">Show guest players</Typography>}
                            />
                          </Box>

                          {/* While we don't yet know whether an upcoming league day exists, hold off on
                              rendering Section B/C — otherwise the last-day recap flashes briefly before
                              the next-day simulation (once loaded) replaces it, which reads as a glitch. */}
                          {upcomingLeagueDayMatchesLoading && (
                            <Box sx={{ py: 4, px: { xs: 1, sm: 4 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Loading insights…</Typography>
                                <Typography variant="caption" color="text.secondary">{upcomingLeagueDayMatchesProgress}%</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={upcomingLeagueDayMatchesProgress} sx={{ borderRadius: 1, height: 6 }} />
                            </Box>
                          )}

                          {/* ── Section B: Last league day what-if (hidden once next league day's draw is out — focus shifts to the future) ── */}
                          {!upcomingLeagueDayMatchesLoading && upcomingLeagueDayMatches.length === 0 && (
                            <>
                          {(() => {
                            const marqueeText = buildBetResultsMarqueeText(leagueDayBetResults);
                            if (!marqueeText) return null;
                            const hasWinner = leagueDayBetResults.some(r => r.bonusPoints > 0);
                            return (
                              <Box
                                sx={{
                                  bgcolor: hasWinner ? '#2e7d32' : 'grey.700',
                                  color: 'white',
                                  py: 1,
                                  mb: 2,
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    animation: 'betResultsMarquee 18s linear infinite',
                                    paddingLeft: '100%',
                                    '@keyframes betResultsMarquee': {
                                      '0%': { transform: 'translateX(0)' },
                                      '100%': { transform: 'translateX(-100%)' },
                                    },
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 700, display: 'inline-block' }}>
                                    {marqueeText}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })()}
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            What if last league day went differently?
                          </Typography>
                          {myCompletedLeagueDayOptions.length > 1 && (
                            <FormControl size="small" sx={{ mb: 1.5, minWidth: 260 }}>
                              <InputLabel id="results-league-day-select-label">League day</InputLabel>
                              <Select
                                labelId="results-league-day-select-label"
                                label="League day"
                                value={selectedResultsLeagueDayId}
                                onChange={(e) => selectResultsLeagueDay(e.target.value as string)}
                              >
                                {myCompletedLeagueDayOptions.map((opt) => (
                                  <MenuItem key={opt.id} value={opt.id}>
                                    {(() => { try { return opt.date ? new Date(opt.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date'; } catch { return 'Unknown date'; } })()}
                                    {opt.division !== player?.division ? ` — Guest in ${opt.division}` : ` — ${opt.division}`}
                                    {opt.isBetOnly ? ' (guessed, didn\'t play)' : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          {lastLeagueDayMatches.length === 0 ? (
                            <Alert severity="info">No completed league days found yet.</Alert>
                          ) : (
                            <>
                              {resultsDivisionInfo?.isGuest && (
                                <Alert severity="info" icon={<span>🎒</span>} sx={{ mb: 1.5 }}>
                                  You played as a guest in <strong>{resultsDivisionInfo.division}</strong> this league day —
                                  the rankings below are for {resultsDivisionInfo.division}, not your own division.
                                </Alert>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Edit any match score below to see how the ranking would have changed.
                                {lastLeagueDayDate && (
                                  <> League day: <strong>{(() => { try { return new Date(lastLeagueDayDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } })()}</strong></>
                                )}
                              </Typography>
                              {lastLeagueDayVenue?.venueName && (
                                <VenueBadge
                                  venueName={lastLeagueDayVenue.venueName}
                                  venueAddress={lastLeagueDayVenue.venueAddress}
                                  variant="body2"
                                />
                              )}

                              {/* Mobile/tablet tabs — hidden on lg+ where layout is side-by-side */}
                              <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 1 }}>
                                <Tabs value={lastDayTab} onChange={(_, v) => setLastDayTab(v)} variant="fullWidth">
                                  <Tab label="Scores" />
                                  <Tab label={hasChanges ? `Ranking ●` : 'Ranking'} sx={hasChanges ? { color: 'warning.main' } : {}} />
                                </Tabs>
                              </Box>

                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { xs: 'stretch', lg: 'flex-start' } }}>
                              {/* Left: Matches grouped by round */}
                              <Box sx={{ flex: '0 0 auto', maxWidth: { lg: 500, xl: 760 }, display: { xs: lastDayTab === 0 ? 'block' : 'none', lg: 'block' } }}>
                              {Array.from(new Set(lastLeagueDayMatches.filter((m: any) => m.isCompleted && m.score?.winnerPair).map((m: any) => m.roundNumber))).sort((a: any, b: any) => a - b).map((round: any) => {
                                const roundMatches = lastLeagueDayMatches.filter((m: any) => m.isCompleted && m.score?.winnerPair && m.roundNumber === round);
                                return (
                                  <Box key={round} sx={{ mb: 2 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                      Round {round}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                      {roundMatches.map((match: any) => {
                                        const override = scoreOverrides[match.id];
                                        const origP1g: number = match.score?.pair1Games ?? 0;
                                        const origP2g: number = match.score?.pair2Games ?? 0;
                                        const p1g = override?.pair1Games ?? origP1g;
                                        const p2g = override?.pair2Games ?? origP2g;
                                        const simWinner = p1g > p2g ? 1 : p2g > p1g ? 2 : 0;
                                        const changed = override && (p1g !== origP1g || p2g !== origP2g);
                                        const maxScore: number = match.roundNumber >= 4 ? 5 : 4;
                                        // Only shown if the player actually submitted a bet for this league day
                                        const myBetPrediction = lastDayBetInfo?.bet?.predictions?.[match.id];
                                        const myBetCorrect = !!myBetPrediction && myBetPrediction.pair1Games === origP1g && myBetPrediction.pair2Games === origP2g;

                                        const updateScore = (field: 'pair1Games' | 'pair2Games', val: number) => {
                                          const clamped = Math.min(maxScore, Math.max(0, val));
                                          setScoreOverrides(prev => ({
                                            ...prev,
                                            [match.id]: {
                                              pair1Games: field === 'pair1Games' ? clamped : (prev[match.id]?.pair1Games ?? origP1g),
                                              pair2Games: field === 'pair2Games' ? clamped : (prev[match.id]?.pair2Games ?? origP2g),
                                            }
                                          }));
                                        };

                                        const resetScore = () => {
                                          setScoreOverrides(prev => {
                                            const next = { ...prev };
                                            delete next[match.id];
                                            return next;
                                          });
                                        };

                                        return (
                                          <Paper key={match.id} variant="outlined" sx={{ p: 1.5, borderColor: changed ? 'warning.main' : 'divider', width: 240, flexShrink: 0 }}>
                                            {/* Pair 1 row */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, overflow: 'hidden' }}>
                                              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 1 ? 700 : 400, color: simWinner === 1 ? 'success.main' : simWinner === 2 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {simWinner === 1 ? '✓ ' : simWinner === 2 ? '✗ ' : ''}{match.pair1.player1Name}
                                                </Typography>
                                                <Typography component="span" variant="body2" sx={{ flexShrink: 0, mx: 0.25 }}>&</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 1 ? 700 : 400, color: simWinner === 1 ? 'success.main' : simWinner === 2 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {match.pair1.player2Name}
                                                </Typography>
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => updateScore('pair1Games', Math.max(0, p1g - 1))}><RemoveIcon fontSize="inherit" /></IconButton>
                                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{p1g}</Typography>
                                                <IconButton size="small" disabled={p1g >= maxScore} onClick={() => updateScore('pair1Games', Math.min(maxScore, p1g + 1))}><AddIcon fontSize="inherit" /></IconButton>
                                              </Box>
                                            </Box>
                                            {/* Pair 2 row */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 2 ? 700 : 400, color: simWinner === 2 ? 'success.main' : simWinner === 1 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {simWinner === 2 ? '✓ ' : simWinner === 1 ? '✗ ' : ''}{match.pair2.player1Name}
                                                </Typography>
                                                <Typography component="span" variant="body2" sx={{ flexShrink: 0, mx: 0.25 }}>&</Typography>
                                                <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 2 ? 700 : 400, color: simWinner === 2 ? 'success.main' : simWinner === 1 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {match.pair2.player2Name}
                                                </Typography>
                                              </Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => updateScore('pair2Games', Math.max(0, p2g - 1))}><RemoveIcon fontSize="inherit" /></IconButton>
                                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{p2g}</Typography>
                                                <IconButton size="small" disabled={p2g >= maxScore} onClick={() => updateScore('pair2Games', Math.min(maxScore, p2g + 1))}><AddIcon fontSize="inherit" /></IconButton>
                                              </Box>
                                            </Box>
                                            {myBetPrediction && (
                                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                <Tooltip title={myBetCorrect ? 'You predicted this correctly!' : `Your guess: ${myBetPrediction.pair1Games}-${myBetPrediction.pair2Games}`}>
                                                  <Chip label={myBetCorrect ? 'Guess ✓' : 'Guess ✗'} size="small" color={myBetCorrect ? 'success' : 'error'} variant="outlined" />
                                                </Tooltip>
                                              </Box>
                                            )}
                                            {changed && (
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                <Chip label={simWinner !== 0 && simWinner !== match.score?.winnerPair ? 'outcome changed' : 'score modified'} size="small" color="warning" />
                                                <Tooltip title="Reset to actual score">
                                                  <IconButton size="small" onClick={resetScore}><ResetIcon fontSize="inherit" /></IconButton>
                                                </Tooltip>
                                              </Box>
                                            )}
                                          </Paper>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })}

                              </Box>{/* end left matches column */}

                              {/* Right: Animated ranking table */}
                              <Box sx={{ flex: 1, minWidth: { xs: 0, lg: 280 }, display: { xs: lastDayTab === 1 ? 'block' : 'none', lg: 'block' } }}>
                              {(() => {
                                const ROW_HEIGHT = 48;
                                const currentRankById: Record<string, number> = {};
                                currentRanked.forEach((p, i) => { currentRankById[p.playerId] = i; });
                                const simRankById: Record<string, number> = {};
                                simRanked.forEach((p, i) => { simRankById[p.playerId] = i; });
                                const simScoreById: Record<string, number> = {};
                                simRanked.forEach(p => { simScoreById[p.playerId] = hasChanges ? (p.simScore ?? 0) : ((p as any).hybridScore ?? 0); });
                                return (
                                  <Box sx={{ position: 'relative', height: currentRanked.length * ROW_HEIGHT, mt: 1 }}>
                                    {currentRanked.map((player) => {
                                      const currentIdx = currentRankById[player.playerId] ?? 999;
                                      const displayIdx = simRankById[player.playerId] ?? currentIdx;
                                      const movement = currentIdx - displayIdx; // positive = moved up
                                      const isMe = player.playerId === myId;
                                      const isAffected = hasChanges && !!matchDeltas[player.playerId];
                                      const score = simScoreById[player.playerId] ?? 0;
                                      return (
                                        <Box
                                          key={player.playerId}
                                          sx={{
                                            position: 'absolute',
                                            top: displayIdx * ROW_HEIGHT,
                                            width: '100%',
                                            height: ROW_HEIGHT - 4,
                                            transition: 'top 0.4s cubic-bezier(0.4,0,0.2,1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            bgcolor: isMe ? 'rgba(25,118,210,0.06)' : 'background.paper',
                                            borderColor: isMe ? 'primary.main' : isAffected ? 'warning.main' : 'divider',
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ width: 28, minWidth: 28, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}>
                                            #{displayIdx + 1}
                                          </Typography>
                                          <Typography variant="body2" sx={{ flex: 1, minWidth: 40, fontWeight: isMe ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {player.playerName}{isMe ? ' (you)' : ''}
                                            {((player as any).isGuestPlayer || (player as any).isGuest) && (
                                              <Chip label="Guest" size="small" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: 9.5, '& .MuiChip-label': { px: 0.5 } }} />
                                            )}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ width: 36, minWidth: 36, flexShrink: 0, textAlign: 'right' }}>
                                            {score.toFixed(1)}
                                          </Typography>
                                          <Box sx={{ width: 48, minWidth: 48, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                            {hasChanges && movement > 0 && <Chip label={`↑${movement}`} size="small" color="success" sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />}
                                            {hasChanges && movement < 0 && <Chip label={`↓${Math.abs(movement)}`} size="small" color="error" sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />}
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}
                              {!hasChanges && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  Adjust scores above — the ranking will animate live.
                                </Typography>
                              )}
                              </Box>{/* end right ranking column */}
                              </Box>{/* end responsive row */}
                            </>
                          )}
                            </>
                          )}

                          {/* ── Section C: Upcoming league day simulation ── */}
                          {upcomingLeagueDayMatches.length > 0 && (() => {
                            // When the player is guesting in another division for this day, simulate against that
                            // division's rankings instead of their home division's pool.
                            const effectiveRankingPool = upcomingDivisionInfo?.isGuest
                              ? (showGuestPlayers ? upcomingDivisionRankings : upcomingDivisionRankings.filter(p => !p.isGuestPlayer && !p.isGuest))
                              : rankingPool;
                            // On the season's first league day nobody has played yet — list the whole pool at 0
                            // so entered scores can start moving players.
                            const playedRanked = effectiveRankingPool.filter(p => p.gamesPlayed > 0);
                            const effectiveCurrentRanked = playedRanked.length > 0 ? playedRanked : effectiveRankingPool;

                            const upcomingDeltas: Record<string, GameDelta> = {};
                            let upcomingSet = 0;
                            upcomingLeagueDayMatches.forEach((match: any) => {
                              const scores = upcomingScores[match.id];
                              if (!scores) return;
                              const p1g = scores.pair1Games;
                              const p2g = scores.pair2Games;
                              if (p1g === 0 && p2g === 0) return; // nothing set yet
                              upcomingSet++;
                              const pair1Ids: string[] = [match.pair1.player1Id, match.pair1.player2Id].filter(Boolean);
                              const pair2Ids: string[] = [match.pair2.player1Id, match.pair2.player2Id].filter(Boolean);
                              // Starting from 0 — add the full game counts as deltas
                              pair1Ids.forEach((pid: string) => {
                                if (!upcomingDeltas[pid]) upcomingDeltas[pid] = { gamesWonDelta: 0, gamesLostDelta: 0 };
                                upcomingDeltas[pid].gamesWonDelta += p1g;
                                upcomingDeltas[pid].gamesLostDelta += p2g;
                              });
                              pair2Ids.forEach((pid: string) => {
                                if (!upcomingDeltas[pid]) upcomingDeltas[pid] = { gamesWonDelta: 0, gamesLostDelta: 0 };
                                upcomingDeltas[pid].gamesWonDelta += p2g;
                                upcomingDeltas[pid].gamesLostDelta += p1g;
                              });
                            });

                            const hasUpcomingSet = upcomingSet > 0;
                            // Constrain to the same player set as effectiveCurrentRanked — a player making their
                            // season debut in the upcoming matches would otherwise flip from 0 to >0 games played
                            // and slot into the simulated list without ever having been in the current one,
                            // shifting everyone below them and breaking the rank-position alignment between lists.
                            const currentRankedIds = new Set(effectiveCurrentRanked.map(p => p.playerId));
                            const upcomingSimRanked = hasUpcomingSet
                              ? recomputeHybridRankings(effectiveRankingPool, upcomingDeltas).filter(p => currentRankedIds.has(p.playerId))
                              : effectiveCurrentRanked;
                            const myUpcomingPos = upcomingSimRanked.findIndex(p => p.playerId === myId);
                            const myUpcomingScore = hasUpcomingSet ? (upcomingSimRanked[myUpcomingPos]?.simScore ?? 0) : 0;

                            // A match counts as "predicted" once one side reaches the winning score (mirrors real completion rules)
                            const allMatchesPredicted = upcomingLeagueDayMatches.every((match: any) => {
                              const s = upcomingScores[match.id];
                              if (!s) return false;
                              const maxScore = match.roundNumber >= 4 ? 5 : 4;
                              return s.pair1Games === maxScore || s.pair2Games === maxScore;
                            });
                            const betLocked = !!upcomingBetInfo?.bet && !isEditingBet;

                            return (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                  What if the next league day goes like this?
                                </Typography>
                                {upcomingEligibleEntries.length > 1 && (
                                  <FormControl size="small" sx={{ mb: 1.5, minWidth: 260 }}>
                                    <InputLabel id="upcoming-league-day-select-label">League day</InputLabel>
                                    <Select
                                      labelId="upcoming-league-day-select-label"
                                      label="League day"
                                      value={selectedUpcomingLeagueDayId}
                                      onChange={(e) => selectUpcomingLeagueDay(e.target.value as string)}
                                    >
                                      {upcomingEligibleEntries.map((entry: any) => {
                                        const id = upcomingEntryId(entry);
                                        const isGuestOption = entry.leagueDay.division !== player?.division;
                                        return (
                                          <MenuItem key={id} value={id}>
                                            {(() => { try { return entry.leagueDay.date ? new Date(entry.leagueDay.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date'; } catch { return 'Unknown date'; } })()}
                                            {isGuestOption ? ` — Guest in ${entry.leagueDay.division}` : ` — ${entry.leagueDay.division}`}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  </FormControl>
                                )}
                                {upcomingDivisionInfo?.isGuest && (
                                  <Alert severity="info" icon={<span>🎒</span>} sx={{ mb: 1.5 }}>
                                    You're playing as a guest in <strong>{upcomingDivisionInfo.division}</strong> this league day —
                                    the rankings and guessing below are for {upcomingDivisionInfo.division}, not your own division.
                                  </Alert>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Set hypothetical scores to preview how your ranking would change.
                                  {upcomingLeagueDayDate && (
                                    <> Upcoming: <strong>{(() => { try { return new Date(upcomingLeagueDayDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } })()}</strong></>
                                  )}
                                </Typography>
                                {upcomingLeagueDayVenue?.venueName && (
                                  <VenueBadge
                                    venueName={upcomingLeagueDayVenue.venueName}
                                    venueAddress={upcomingLeagueDayVenue.venueAddress}
                                    variant="body2"
                                  />
                                )}

                                {/* Mobile/tablet tabs for upcoming section — hidden on lg+ where layout is side-by-side */}
                                <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 1 }}>
                                  <Tabs value={upcomingTab} onChange={(_, v) => setUpcomingTab(v)} variant="fullWidth">
                                    <Tab label="Scores" />
                                    <Tab label={hasUpcomingSet ? `Ranking ●` : 'Ranking'} sx={hasUpcomingSet ? { color: 'primary.main' } : {}} />
                                  </Tabs>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { xs: 'stretch', lg: 'flex-start' } }}>
                                {/* Left: Matches grouped by round */}
                                <Box sx={{ flex: '0 0 auto', maxWidth: { lg: 500, xl: 760 }, display: { xs: upcomingTab === 0 ? 'block' : 'none', lg: 'block' } }}>
                                {Array.from(new Set(upcomingLeagueDayMatches.map((m: any) => m.roundNumber))).sort((a: any, b: any) => a - b).map((round: any) => {
                                  const roundMatches = upcomingLeagueDayMatches.filter((m: any) => m.roundNumber === round);
                                  return (
                                    <Box key={round} sx={{ mb: 2 }}>
                                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Round {round}
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                        {roundMatches.map((match: any) => {
                                          const scores = upcomingScores[match.id];
                                          const p1g = scores?.pair1Games ?? 0;
                                          const p2g = scores?.pair2Games ?? 0;
                                          const simWinner = p1g > p2g ? 1 : p2g > p1g ? 2 : 0;
                                          const maxScore: number = match.roundNumber >= 4 ? 5 : 4;
                                          const hasScoreSet = p1g !== 0 || p2g !== 0;

                                          const updateUpcomingScore = (field: 'pair1Games' | 'pair2Games', val: number) => {
                                            const clamped = Math.min(maxScore, Math.max(0, val));
                                            setUpcomingScores(prev => ({
                                              ...prev,
                                              [match.id]: {
                                                pair1Games: field === 'pair1Games' ? clamped : (prev[match.id]?.pair1Games ?? 0),
                                                pair2Games: field === 'pair2Games' ? clamped : (prev[match.id]?.pair2Games ?? 0),
                                              }
                                            }));
                                          };

                                          const resetUpcomingScore = () => {
                                            setUpcomingScores(prev => {
                                              const next = { ...prev };
                                              delete next[match.id];
                                              return next;
                                            });
                                          };

                                          return (
                                            <Paper key={match.id} variant="outlined" sx={{ p: 1.5, borderColor: simWinner !== 0 ? 'primary.main' : 'divider', width: 240, flexShrink: 0 }}>
                                              {/* Pair 1 */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, overflow: 'hidden' }}>
                                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 1 ? 700 : 400, color: simWinner === 1 ? 'success.main' : simWinner === 2 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {simWinner === 1 ? '✓ ' : simWinner === 2 ? '✗ ' : ''}{match.pair1.player1Name}
                                                  </Typography>
                                                  <Typography component="span" variant="body2" sx={{ flexShrink: 0, mx: 0.25 }}>&</Typography>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 1 ? 700 : 400, color: simWinner === 1 ? 'success.main' : simWinner === 2 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {match.pair1.player2Name}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <IconButton size="small" disabled={betLocked} onClick={() => updateUpcomingScore('pair1Games', Math.max(0, p1g - 1))}><RemoveIcon fontSize="inherit" /></IconButton>
                                                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{p1g}</Typography>
                                                  <IconButton size="small" disabled={betLocked || p1g >= maxScore} onClick={() => updateUpcomingScore('pair1Games', Math.min(maxScore, p1g + 1))}><AddIcon fontSize="inherit" /></IconButton>
                                                </Box>
                                              </Box>
                                              {/* Pair 2 */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}>
                                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 2 ? 700 : 400, color: simWinner === 2 ? 'success.main' : simWinner === 1 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {simWinner === 2 ? '✓ ' : simWinner === 1 ? '✗ ' : ''}{match.pair2.player1Name}
                                                  </Typography>
                                                  <Typography component="span" variant="body2" sx={{ flexShrink: 0, mx: 0.25 }}>&</Typography>
                                                  <Typography component="span" variant="body2" sx={{ fontWeight: simWinner === 2 ? 700 : 400, color: simWinner === 2 ? 'success.main' : simWinner === 1 ? 'text.disabled' : 'text.primary', flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {match.pair2.player2Name}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <IconButton size="small" disabled={betLocked} onClick={() => updateUpcomingScore('pair2Games', Math.max(0, p2g - 1))}><RemoveIcon fontSize="inherit" /></IconButton>
                                                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{p2g}</Typography>
                                                  <IconButton size="small" disabled={betLocked || p2g >= maxScore} onClick={() => updateUpcomingScore('pair2Games', Math.min(maxScore, p2g + 1))}><AddIcon fontSize="inherit" /></IconButton>
                                                </Box>
                                              </Box>
                                              {hasScoreSet && !betLocked && (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                  <Tooltip title="Reset to 0-0">
                                                    <IconButton size="small" onClick={resetUpcomingScore}><ResetIcon fontSize="inherit" /></IconButton>
                                                  </Tooltip>
                                                </Box>
                                              )}
                                            </Paper>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                })}

                                </Box>{/* end left matches column */}

                                {/* Right: Animated ranking table for upcoming simulation */}
                                <Box sx={{ flex: 1, minWidth: { xs: 0, lg: 280 }, display: { xs: upcomingTab === 1 ? 'block' : 'none', lg: 'block' } }}>
                                {(() => {
                                  const ROW_HEIGHT = 48;
                                  const currentRankById: Record<string, number> = {};
                                  effectiveCurrentRanked.forEach((p, i) => { currentRankById[p.playerId] = i; });
                                  const upcomingRankById: Record<string, number> = {};
                                  upcomingSimRanked.forEach((p, i) => { upcomingRankById[p.playerId] = i; });
                                  const upcomingScoreById: Record<string, number> = {};
                                  upcomingSimRanked.forEach(p => { upcomingScoreById[p.playerId] = hasUpcomingSet ? (p.simScore ?? 0) : ((p as any).hybridScore ?? 0); });
                                  return (
                                    <Box sx={{ position: 'relative', height: effectiveCurrentRanked.length * ROW_HEIGHT, mt: 1 }}>
                                      {effectiveCurrentRanked.map((player) => {
                                        const currentIdx = currentRankById[player.playerId] ?? 999;
                                        const displayIdx = upcomingRankById[player.playerId] ?? currentIdx;
                                        const movement = currentIdx - displayIdx;
                                        const isMe = player.playerId === myId;
                                        const isAffected = hasUpcomingSet && !!upcomingDeltas[player.playerId];
                                        const score = upcomingScoreById[player.playerId] ?? 0;
                                        return (
                                          <Box
                                            key={player.playerId}
                                            sx={{
                                              position: 'absolute',
                                              top: displayIdx * ROW_HEIGHT,
                                              width: '100%',
                                              height: ROW_HEIGHT - 4,
                                              transition: 'top 0.4s cubic-bezier(0.4,0,0.2,1)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              px: 1,
                                              borderRadius: 1,
                                              border: '1px solid',
                                              bgcolor: isMe ? 'rgba(25,118,210,0.06)' : 'background.paper',
                                              borderColor: isMe ? 'primary.main' : isAffected ? 'warning.main' : 'divider',
                                            }}
                                          >
                                            <Typography variant="body2" sx={{ width: 28, minWidth: 28, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}>
                                              #{displayIdx + 1}
                                            </Typography>
                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 40, fontWeight: isMe ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {player.playerName}{isMe ? ' (you)' : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: 36, minWidth: 36, flexShrink: 0, textAlign: 'right' }}>
                                              {score.toFixed(1)}
                                            </Typography>
                                            <Box sx={{ width: 48, minWidth: 48, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                              {hasUpcomingSet && movement > 0 && <Chip label={`↑${movement}`} size="small" color="success" sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />}
                                              {hasUpcomingSet && movement < 0 && <Chip label={`↓${Math.abs(movement)}`} size="small" color="error" sx={{ fontWeight: 700, height: 20, fontSize: 11 }} />}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  );
                                })()}
                                {!hasUpcomingSet && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Set scores above — the ranking will animate live.
                                  </Typography>
                                )}
                                </Box>{/* end right ranking column */}
                                </Box>{/* end responsive row */}

                                {/* ── Betting on the upcoming league day ── */}
                                {upcomingBetInfo && (
                                  <>
                                    <Divider sx={{ my: 2 }} />
                                    {upcomingBetInfo.eligibility.eligible ? (
                                      <Box>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                          🎲 Submit your guess
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                          Predict every match above. For each round you get completely right, you earn 1 bonus point (max 4).
                                        </Typography>
                                        {betSubmitError && (
                                          <Alert severity="error" sx={{ mb: 1.5 }}>{betSubmitError}</Alert>
                                        )}
                                        {upcomingBetInfo.bet && !isEditingBet ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Chip label="Guess submitted ✓" color="success" />
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => {
                                                setUpcomingScores(upcomingBetInfo.bet!.predictions);
                                                setIsEditingBet(true);
                                              }}
                                            >
                                              Edit my guess
                                            </Button>
                                          </Box>
                                        ) : (
                                          <>
                                            <Button
                                              variant="contained"
                                              disabled={!allMatchesPredicted || betSubmitting}
                                              onClick={submitBet}
                                            >
                                              {betSubmitting ? 'Submitting…' : (upcomingBetInfo.bet ? 'Save changes' : 'Submit my guess')}
                                            </Button>
                                            {!allMatchesPredicted && (
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                Set a final score for every match above to submit your guess.
                                              </Typography>
                                            )}
                                          </>
                                        )}
                                      </Box>
                                    ) : (
                                      <Alert severity="info">{upcomingBetInfo.eligibility.reason}</Alert>
                                    )}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}

              {/* Season Stats by Division */}
              {allSeasons && allSeasons.length > 1 && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            📊 {selectedSeason?.name || "Season"} - Stats by Division
                          </Typography>
                        </Box>
                        {playerHistory?.divisionStats &&
                        Object.keys(playerHistory.divisionStats).length > 0 && (
                        <Grid container spacing={2}>
                          {Object.entries(playerHistory.divisionStats)
                            .sort((a, b) => {
                              // Sort divisions in order
                              const order = [
                                "Master",
                                "Division 1",
                                "Division 2",
                                "Division 3",
                                "Women Division",
                                "Women Division 2",
                              ];
                              return order.indexOf(a[0]) - order.indexOf(b[0]);
                            })
                            .map(([divisionName, stats]) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={divisionName}
                              >
                                <Paper
                                  sx={{
                                    p: 2,
                                    border: "2px solid",
                                    borderColor:
                                      divisionName === seasonDivision
                                        ? "primary.main"
                                        : "divider",
                                    bgcolor:
                                      divisionName === seasonDivision
                                        ? "rgba(25, 118, 210, 0.05)"
                                        : "background.paper",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1.5,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "bold",
                                        color:
                                          divisionName === seasonDivision
                                            ? "primary.main"
                                            : "text.primary",
                                      }}
                                    >
                                      {divisionName === "Women Division"
                                        ? "Women Division 1"
                                        : divisionName}
                                    </Typography>
                                    {divisionName === seasonDivision && (
                                      <Chip
                                        label={selectedSeason?.isActive ? "Current" : "Back Then"}
                                        color="primary"
                                        size="small"
                                      />
                                    )}
                                  </Box>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Box
                                        sx={{
                                          textAlign: "center",
                                          p: 1,
                                          bgcolor: "info.light",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: currentOrg?.slug === "padel-eagle" ? "#743444" : "#1976d2",
                                          }}
                                        >
                                          {stats.totalPoints || 0}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Points
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Box
                                        sx={{
                                          textAlign: "center",
                                          p: 1,
                                          bgcolor: "warning.light",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "#ff9800",
                                          }}
                                        >
                                          {stats.gamesPlayed || 0}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Games
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Box
                                        sx={{
                                          textAlign: "center",
                                          p: 1,
                                          bgcolor: "success.light",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "#4caf50",
                                          }}
                                        >
                                          {stats.gamesWon || 0}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Wins
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Box
                                        sx={{
                                          textAlign: "center",
                                          p: 1,
                                          bgcolor: "error.light",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: "bold",
                                            color: "#f44336",
                                          }}
                                        >
                                          {stats.gamesLost || 0}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Losses
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                  <Divider sx={{ my: 1.5 }} />
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Matches:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                      >
                                        {(stats.matchesWon || 0) +
                                          (stats.matchesLost || 0)}{" "}
                                        ({stats.matchesWon || 0}W-
                                        {stats.matchesLost || 0}L)
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Win Rate:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                      >
                                        {stats.gamesPlayed > 0
                                          ? `${((stats.gamesWon / stats.gamesPlayed) * 100).toFixed(1)}%`
                                          : "0%"}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        League Days:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                      >
                                        {stats.leagueDaysPlayed || 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                        </Grid>
                        )}
                        {(!playerHistory?.divisionStats ||
                          Object.keys(playerHistory.divisionStats).length === 0) && (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                            No stats recorded for this season yet.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

              {/* Season Badges */}
              {selectedSeason && player?.badges && player.badges.length > 0 && (() => {
                const seasonStart = selectedSeason.startDate ? new Date(selectedSeason.startDate).getTime() : null;
                const seasonEnd = selectedSeason.endDate ? new Date(selectedSeason.endDate).getTime() + 86400000 : null;

                const seasonBadges = player.badges.filter((badge) => {
                  // Primary: top-level seasonId (set for all badges by badgeSystem)
                  if (badge.seasonId) {
                    return badge.seasonId === selectedSeason.id;
                  }
                  // Secondary: metadata.seasonId (century + all_season_warrior also set this)
                  if (badge.metadata?.seasonId) {
                    return badge.metadata.seasonId === selectedSeason.id;
                  }
                  // Last resort: date range fallback for legacy badges without any seasonId
                  if (seasonStart !== null && badge.earnedAt) {
                    const earnedMs = new Date(badge.earnedAt).getTime();
                    return earnedMs >= seasonStart && (seasonEnd !== null && earnedMs <= seasonEnd);
                  }
                  return false;
                });

                if (seasonBadges.length === 0) return null;

                return (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                          🏅 Badges Earned — {selectedSeason.name}
                        </Typography>
                        <BadgeDisplay badges={seasonBadges} size="medium" />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}

              {/* Lifetime Stats by Division */}
              {player?.divisionStats &&
                Object.keys(player.divisionStats).length > 0 && (
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        background:
                          brandGradient,
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 2, color: "white" }}
                        >
                          🏆 Lifetime Stats by Division (All Seasons)
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.entries(player.divisionStats)
                            .sort((a, b) => {
                              // Sort divisions in order: Master, Division 1, 2, 2A, 2B, 3, 4, Women Division, Women Division 2
                              const order = [
                                "Master",
                                "Division 1",
                                "Division 2",
                                "Division 2A",
                                "Division 2B",
                                "Division 3",
                                "Division 4",
                                "Women Division",
                                "Women Division 2",
                              ];
                              return order.indexOf(a[0]) - order.indexOf(b[0]);
                            })
                            .map(([divisionName, stats]) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={divisionName}
                              >
                                <Paper
                                  sx={{
                                    p: 2,
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: "bold",
                                      mb: 1.5,
                                      color: "white",
                                      textAlign: "center",
                                      borderBottom:
                                        "2px solid rgba(255,255,255,0.3)",
                                      pb: 1,
                                    }}
                                  >
                                    {divisionName === "Women Division"
                                      ? "Women Division 1"
                                      : divisionName}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.9)" }}
                                      >
                                        Points:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {stats.totalPoints || 0}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.9)" }}
                                      >
                                        Games:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {stats.gamesPlayed || 0} (
                                        {stats.gamesWon || 0}W-
                                        {stats.gamesLost || 0}L)
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.9)" }}
                                      >
                                        Matches:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {(stats.matchesWon || 0) +
                                          (stats.matchesLost || 0)}{" "}
                                        ({stats.matchesWon || 0}W-
                                        {stats.matchesLost || 0}L)
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.9)" }}
                                      >
                                        Win Rate:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {stats.gamesPlayed > 0
                                          ? `${((stats.gamesWon / stats.gamesPlayed) * 100).toFixed(1)}%`
                                          : "0%"}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.9)" }}
                                      >
                                        League Days:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "white",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {stats.leagueDaysPlayed || 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

              {/* Match History */}
              {playerHistory &&
                playerHistory.matches.length > 0 &&
                (() => {
                  // Create a map of leagueDayId to date for easy lookup
                  const leagueDayDateMap = new Map<string, Date>();

                  // Try allDivisionLeagueDays first
                  playerHistory.allDivisionLeagueDays?.forEach((day) => {
                    if (day.id && day.date) {
                      leagueDayDateMap.set(day.id, new Date(day.date));
                    }
                  });

                  // Also add from leagueDayStats as fallback
                  playerHistory.leagueDayStats?.forEach((stat) => {
                    if (
                      stat.leagueDayId &&
                      stat.date &&
                      !leagueDayDateMap.has(stat.leagueDayId)
                    ) {
                      leagueDayDateMap.set(
                        stat.leagueDayId,
                        new Date(stat.date),
                      );
                    }
                  });

                  // Sort matches by date (newest first), then by round number descending
                  const sortedMatches = [...playerHistory.matches].sort(
                    (a, b) => {
                      // Get the actual league day dates for proper sorting
                      const dateA = leagueDayDateMap.get(a.leagueDayId);
                      const dateB = leagueDayDateMap.get(b.leagueDayId);

                      // If both have valid dates, compare them
                      if (dateA && dateB) {
                        const dateDiff = dateB.getTime() - dateA.getTime();

                        // If dates are equal, sort by round number descending (4, 3, 2, 1)
                        if (dateDiff === 0) {
                          return (b.roundNumber || 0) - (a.roundNumber || 0);
                        }

                        return dateDiff;
                      }

                      // Fallback to scoredAt/createdAt if league day date not available
                      const fallbackDateA =
                        a.scoredAt || a.createdAt || new Date(0);
                      const fallbackDateB =
                        b.scoredAt || b.createdAt || new Date(0);
                      const dateDiff =
                        new Date(fallbackDateB).getTime() -
                        new Date(fallbackDateA).getTime();

                      if (dateDiff === 0) {
                        return (b.roundNumber || 0) - (a.roundNumber || 0);
                      }

                      return dateDiff;
                    },
                  );

                  // Group matches by league day date
                  const matchesByDate = new Map<string, typeof sortedMatches>();
                  sortedMatches.forEach((match) => {
                    const leagueDayDate = leagueDayDateMap.get(
                      match.leagueDayId,
                    );
                    const dateKey =
                      leagueDayDate && !isNaN(leagueDayDate.getTime())
                        ? formatLeagueDayDateCustom(leagueDayDate, {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }, "en-GB")
                        : "Date unknown";

                    if (!matchesByDate.has(dateKey)) {
                      matchesByDate.set(dateKey, []);
                    }
                    matchesByDate.get(dateKey)!.push(match);
                  });

                  return (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", mb: 2 }}
                          >
                            📈 Match History ({sortedMatches.length} matches)
                          </Typography>

                          {Array.from(matchesByDate.entries()).map(
                            ([dateKey, matches], index) => {
                              // Calculate wins for this date
                              const winsOnDate = matches.filter((match) => {
                                const isPair1 =
                                  match.pair1.player1Id ===
                                    playerUser?.playerId ||
                                  match.pair1.player2Id ===
                                    playerUser?.playerId;
                                return isPair1
                                  ? match.score.winnerPair === 1
                                  : match.score.winnerPair === 2;
                              }).length;

                              return (
                                <Accordion
                                  key={dateKey}
                                  defaultExpanded={index === 0}
                                  sx={{ mb: 1 }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        width: "100%",
                                      }}
                                    >
                                      <Typography sx={{ fontWeight: "bold" }}>
                                        📅 {dateKey}
                                      </Typography>
                                      <Chip
                                        label={`${matches.length} ${matches.length > 1 ? "matches" : "match"}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={`${winsOnDate}W - ${matches.length - winsOnDate}L`}
                                        size="small"
                                        color={
                                          winsOnDate >
                                          matches.length - winsOnDate
                                            ? "success"
                                            : "default"
                                        }
                                      />
                                      {matches.some((m) => m.isTrial) && (
                                        <Chip
                                          label="Trial Day"
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                          title="Trial day — results don't count toward ranking"
                                        />
                                      )}
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                      }}
                                    >
                                      {matches.map((match) => {
                                        const isPair1 =
                                          match.pair1.player1Id ===
                                            playerUser?.playerId ||
                                          match.pair1.player2Id ===
                                            playerUser?.playerId;
                                        const partnerName = isPair1
                                          ? match.pair1.player1Id ===
                                            playerUser?.playerId
                                            ? match.pair1Player2Name
                                            : match.pair1Player1Name
                                          : match.pair2.player1Id ===
                                              playerUser?.playerId
                                            ? match.pair2Player2Name
                                            : match.pair2Player1Name;

                                        const opponent1Name = isPair1
                                          ? match.pair2Player1Name
                                          : match.pair1Player1Name;
                                        const opponent2Name = isPair1
                                          ? match.pair2Player2Name
                                          : match.pair1Player2Name;

                                        const didWin = isPair1
                                          ? match.score.winnerPair === 1
                                          : match.score.winnerPair === 2;

                                        const score = `${match.score.pair1Games}-${match.score.pair2Games}`;

                                        return (
                                          <Paper
                                            key={match.id}
                                            sx={{
                                              p: 2,
                                              backgroundColor: didWin
                                                ? "success.light"
                                                : "error.light",
                                              border: didWin
                                                ? "1px solid"
                                                : "1px solid",
                                              borderColor: didWin
                                                ? "success.main"
                                                : "error.main",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 1,
                                              }}
                                            >
                                              <Chip
                                                label={didWin ? "Won" : "Lost"}
                                                color={
                                                  didWin ? "success" : "error"
                                                }
                                                size="small"
                                                sx={{ fontWeight: "bold" }}
                                              />
                                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {match.isTrial && (
                                                  <Chip
                                                    label="Trial Day"
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                    title="Trial day results don't count toward ranking"
                                                  />
                                                )}
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Round {match.roundNumber}, Court{" "}
                                                  {match.courtNumber}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: "bold",
                                                mb: 0.5,
                                              }}
                                            >
                                              With: {partnerName}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ mb: 0.5 }}
                                            >
                                              vs {opponent1Name} &{" "}
                                              {opponent2Name}
                                            </Typography>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: "bold",
                                                color: didWin
                                                  ? "#2e7d32"
                                                  : "#c62828",
                                              }}
                                            >
                                              {isPair1
                                                ? score
                                                : `${match.score.pair2Games}-${match.score.pair1Games}`}
                                            </Typography>
                                          </Paper>
                                        );
                                      })}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              );
                            },
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}

              {/* Points Progress Chart */}
              {playerHistory && playerHistory.leagueDayStats.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", color: currentOrg?.slug === "padel-eagle" ? "#743444" : "#1976d2" }}
                        >
                          📊 Points Progress
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: currentOrg?.slug === "padel-eagle" ? "#743444" : "#1976d2",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              Played
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "#f44336",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              Absent
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ position: "relative", height: 300, pt: 2 }}>
                        {/* Y-axis labels */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 60,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            pr: 1,
                          }}
                        >
                          {(() => {
                            const maxPoints = Math.max(
                              ...playerHistory.leagueDayStats.map(
                                (s) => s.points,
                              ),
                              1,
                            );
                            const steps = 5;
                            const stepValue = Math.ceil(maxPoints / steps);
                            return Array.from({ length: steps + 1 }, (_, i) => (
                              <Typography
                                key={i}
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                {stepValue * (steps - i)}
                              </Typography>
                            ));
                          })()}
                        </Box>

                        {/* Chart area - Line Graph */}
                        <Box
                          sx={{
                            ml: 4,
                            height: "calc(100% - 60px)",
                            borderLeft: "2px solid #e0e0e0",
                            borderBottom: "2px solid #e0e0e0",
                            position: "relative",
                            px: 1,
                          }}
                        >
                          <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 1000 200"
                            preserveAspectRatio="none"
                            style={{ overflow: "visible" }}
                          >
                            {(() => {
                              const sortedStats = [
                                ...playerHistory.leagueDayStats,
                              ].sort((a, b) => a.dayNumber - b.dayNumber);

                              const maxPoints = Math.max(
                                ...playerHistory.leagueDayStats
                                  .filter((s) => s.participated !== false)
                                  .map((s) => s.points),
                                1,
                              );

                              const chartWidth = 1000;
                              const chartHeight = 200;
                              const padding = 20;
                              const pointSpacing =
                                (chartWidth - 2 * padding) /
                                (sortedStats.length - 1 || 1);

                              // Generate points for the line
                              const points = sortedStats.map((stat, index) => {
                                const x =
                                  sortedStats.length === 1
                                    ? chartWidth / 2
                                    : padding + index * pointSpacing;
                                const y =
                                  chartHeight -
                                  padding -
                                  (stat.points / maxPoints) *
                                    (chartHeight - 2 * padding);
                                return { x, y, stat };
                              });

                              // Create continuous path data connecting all points
                              const pathData = points
                                .map(
                                  (point, index) =>
                                    `${index === 0 ? "M" : "L"} ${point.x} ${
                                      point.y
                                    }`,
                                )
                                .join(" ");

                              return (
                                <>
                                  {/* Continuous line connecting all points */}
                                  <path
                                    d={pathData}
                                    fill="none"
                                    stroke="#1976d2"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                  />

                                  {/* Points */}
                                  {points.map((point, index) => {
                                    const isAbsent =
                                      point.stat.participated === false;
                                    const color = isAbsent
                                      ? "#f44336"
                                      : "#1976d2";

                                    return (
                                      <g key={sortedStats[index].leagueDayId}>
                                        {/* Dot */}
                                        <circle
                                          cx={point.x}
                                          cy={point.y}
                                          r="4"
                                          fill={color}
                                          stroke="white"
                                          strokeWidth="2"
                                          vectorEffect="non-scaling-stroke"
                                          style={{ cursor: "pointer" }}
                                        />
                                        {/* Points label */}
                                        <text
                                          x={point.x}
                                          y={point.y - 15}
                                          textAnchor="middle"
                                          fontSize="12"
                                          fontWeight="bold"
                                          fill={color}
                                          transform={`translate(${point.x}, ${point.y - 15}) scale(1.5, 1) translate(${-point.x}, ${-(point.y - 15)})`}
                                        >
                                          {sortedStats[index].points}
                                        </text>
                                      </g>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </svg>
                        </Box>

                        {/* X-axis labels */}
                        <Box
                          sx={{
                            ml: 4,
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 0.5,
                            px: 1,
                          }}
                        >
                          {[...playerHistory.leagueDayStats]
                            .sort((a, b) => a.dayNumber - b.dayNumber)
                            .map((stat) => {
                              const formattedDate = formatLeagueDayDateCustom(
                                stat.date,
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                },
                                "en-GB"
                              );

                              return (
                                <Box
                                  key={stat.leagueDayId}
                                  sx={{
                                    textAlign: "center",
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: "0.65rem",
                                      display: "block",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Day {stat.dayNumber}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: "0.6rem",
                                      display: "block",
                                    }}
                                  >
                                    {formattedDate}
                                  </Typography>
                                </Box>
                              );
                            })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* League Day Participation */}
              {playerHistory &&
                playerHistory.allDivisionLeagueDays &&
                playerHistory.allDivisionLeagueDays.length > 0 && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 2, color: currentOrg?.slug === "padel-eagle" ? "#743444" : "#1976d2" }}
                        >
                          📅 League Day Participation
                        </Typography>
                        <Grid container spacing={1}>
                          {playerHistory.allDivisionLeagueDays.map(
                            (day: any) => {
                              const formattedDate = formatLeagueDayDateCustom(
                                day.date,
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                },
                                "en-GB"
                              );

                              // Determine status:
                              // - Completed & participated = Played
                              // - Completed & not participated = Absent
                              // - Not completed & on waitlist = On Waitlist
                              // - Not completed & participated = Registered
                              // - Not completed & registration not open = Registration Not Open
                              // - Not completed & registration open & not participated = Not Registered
                              const isIncomplete =
                                !day.isCompleted || day.isDraft;
                              
                              let status: string;
                              if (!isIncomplete) {
                                // Completed league day
                                status = day.participated ? "played" : "absent";
                              } else {
                                // Incomplete league day
                                if (day.isOnWaitlist) {
                                  status = "waitlist";
                                } else if (day.participated) {
                                  status = "registered";
                                } else if (!day.registrationOpen) {
                                  status = "registrationNotOpen";
                                } else {
                                  status = "notRegistered";
                                }
                              }

                              return (
                                <Grid item xs={6} sm={4} md={3} key={day.id}>
                                  <Paper
                                    sx={{
                                      p: 1.5,
                                      textAlign: "center",
                                      backgroundColor:
                                        status === "played"
                                          ? "success.light"
                                          : status === "registered"
                                            ? "warning.light"
                                            : status === "waitlist"
                                              ? "#e1bee7"
                                              : status === "registrationNotOpen"
                                                ? "grey.100"
                                                : status === "notRegistered"
                                                  ? "action.disabledBackground"
                                                  : "error.light",
                                      border: "2px solid",
                                      borderColor:
                                        status === "played"
                                          ? "success.main"
                                          : status === "registered"
                                            ? "warning.main"
                                            : status === "waitlist"
                                              ? "#9c27b0"
                                              : status === "registrationNotOpen"
                                                ? "grey.400"
                                                : status === "notRegistered"
                                                  ? "action.disabled"
                                                  : "error.main",
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: "bold",
                                        color:
                                          status === "played"
                                            ? "#2e7d32"
                                            : status === "registered"
                                              ? "#e65100"
                                              : status === "waitlist"
                                                ? "#7b1fa2"
                                                : status === "registrationNotOpen"
                                                  ? "#757575"
                                                  : status === "notRegistered"
                                                    ? "#616161"
                                                    : "#c62828",
                                        display: "block",
                                        mb: 0.5,
                                      }}
                                    >
                                      Day {day.dayNumber}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.7rem",
                                        color: "text.secondary",
                                        display: "block",
                                        mb: 0.5,
                                      }}
                                    >
                                      {formattedDate}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.65rem",
                                        fontWeight: "bold",
                                        color:
                                          status === "played"
                                            ? "#4caf50"
                                            : status === "registered"
                                              ? "#ffa726"
                                              : status === "waitlist"
                                                ? "#ab47bc"
                                                : status === "registrationNotOpen"
                                                  ? "#9e9e9e"
                                                  : status === "notRegistered"
                                                    ? "#9e9e9e"
                                                    : "#f44336",
                                      }}
                                    >
                                      {status === "played"
                                        ? "✓ Played"
                                        : status === "registered"
                                          ? "📝 Registered"
                                          : status === "waitlist"
                                            ? "⏳ On Waitlist"
                                            : status === "registrationNotOpen"
                                              ? "🔒 Registration Not Open"
                                              : status === "notRegistered"
                                                ? "○ Not Registered"
                                                : "✗ Absent"}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              );
                            },
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
            </>
          )}
          {/* END LEAGUE VIEW CONTENT */}

          {/* TOURNAMENT VIEW CONTENT - Show when in tournament view or not a "both" org */}
          {(currentOrg?.competitionType !== "both" ||
            effectiveDashboardView === "tournament") && (
            <>
              {/* Action needed banner: only when invitations are waiting */}
              {(currentOrg?.competitionType === "tournament" ||
                currentOrg?.competitionType === "both") &&
                pendingInvitationCount > 0 && (
                  <Grid item xs={12}>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        alignItems: "center",
                        ...(currentOrg?.slug === "padel-eagle" && {
                          borderRadius: "16px",
                          bgcolor: "#F4C48B",
                          color: "#743444",
                          border: "1px solid #E49421",
                        }),
                      }}
                      action={
                        tournamentTab !== 0 ? (
                          <Button
                            size="small"
                            onClick={() => setTournamentTab(0)}
                            color="inherit"
                            sx={{ fontWeight: "bold" }}
                          >
                            View
                          </Button>
                        ) : undefined
                      }
                    >
                      <strong>Action needed:</strong> you have{" "}
                      {pendingInvitationCount} tournament invitation
                      {pendingInvitationCount === 1 ? "" : "s"} waiting for
                      your answer.
                    </Alert>
                  </Grid>
                )}

              {/* Dashboard tabs */}
              {(currentOrg?.competitionType === "tournament" ||
                currentOrg?.competitionType === "both") && (
                <Grid item xs={12}>
                  <Tabs
                    value={tournamentTab}
                    onChange={(_, v) => setTournamentTab(v)}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                  >
                    <Tab
                      label={
                        pendingInvitationCount > 0
                          ? `Tournaments (${pendingInvitationCount})`
                          : "Tournaments"
                      }
                    />
                    <Tab label="Performance" />
                  </Tabs>
                </Grid>
              )}

              {/* Tournaments tab: invitations first, then registered tournaments */}
              {(currentOrg?.competitionType === "tournament" ||
                currentOrg?.competitionType === "both") && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: tournamentTab === 0 ? "block" : "none" }}
                >
                  {/* Always mounted so the banner count stays current */}
                  <Box
                    sx={{
                      display: pendingInvitationCount > 0 ? "block" : "none",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", mb: 1.5 }}
                    >
                      Invitations waiting for you
                    </Typography>
                    <InvitationManager
                      key={`inv-${tournamentsRefreshKey}`}
                      hideEmptyState
                      onPendingCountChange={setPendingInvitationCount}
                    />
                  </Box>
                  <PublicTournaments
                    key={`tournaments-${tournamentsRefreshKey}`}
                    title="Your Upcoming Tournaments"
                    embedded
                    onlyRegistered
                    hideGoToDashboardButton
                  />
                </Grid>
              )}

              {/* Performance tab */}
              {(currentOrg?.competitionType === "tournament" ||
                currentOrg?.competitionType === "both") &&
                player?.id &&
                tournamentTab === 1 && (
                  <Grid item xs={12}>
                    <TournamentPerformanceSection playerId={player.id} />
                  </Grid>
                )}

              {/* TODO: Add tournament-specific stats, match history, and participation cards here */}
              {/* For now, tournament view only shows invitations */}
            </>
          )}
          {/* END TOURNAMENT VIEW CONTENT */}
        </Grid>
      </Container>

      {/* Forced Password Change Dialog */}
      <Dialog
        open={player?.requirePasswordChange === true}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Password Change Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You are using a temporary password. Please change it to continue.
          </Alert>

          {passwordChangeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordChangeError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
            helperText="At least 6 characters"
            disabled={changingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            disabled={changingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={changingPassword || !newPassword || !confirmPassword}
            fullWidth
            sx={{ m: 2 }}
          >
            {changingPassword ? (
              <CircularProgress size={24} />
            ) : (
              "Change Password"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Season Registration Dialog */}
      {player && playerToken && (
        <PlayerSeasonRegistrationDialog
          open={registrationDialogOpen}
          onClose={() => setRegistrationDialogOpen(false)}
          player={player}
          playerToken={playerToken}
          onSuccess={checkRegistrationStatus}
        />
      )}

      {/* Gear Order Edit Dialog */}
      <GearOrderEditDialog
        open={gearOrderDialogOpen}
        onClose={() => setGearOrderDialogOpen(false)}
        fetchFn={apiRequest}
        getEndpoint="/api/season-registration/gear-order"
        updateEndpoint="/api/season-registration/gear-order"
      />

      {/* Padel Eagle welcome dialog (currently disabled) */}
      <Dialog
        // Disabled: Padel Eagle does not want the new-player welcome dialog
        open={false}
        onClose={() => setWelcomeDialogHidden(true)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>👋 Welcome to your Dashboard!</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Here's everything you need to know to get started.
          </Typography>
            <Typography variant="overline" sx={{ fontWeight: "bold", color: "#743444", letterSpacing: 1 }}>📈 What you'll see once you start playing</Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 2, "& li": { mb: 0.75 } }}>
              <li><Typography variant="body2">📊 <strong>Performance Insights</strong> — attendance rate, win rate and points trend over the season.</Typography></li>
              <li><Typography variant="body2">🏆 <strong>Points Breakdown</strong> — how your ranking points are calculated game by game.</Typography></li>
              <li><Typography variant="body2">📅 <strong>Match History</strong> — every match you've played, with partners, opponents and scores.</Typography></li>
              <li><Typography variant="body2">🏅 <strong>Badges</strong> — achievements for milestones like first win, consecutive attendance, and more.</Typography></li>
            </Box>
            <Typography variant="overline" sx={{ fontWeight: "bold", color: "#743444", letterSpacing: 1 }}>🧭 Getting around</Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 2, "& li": { mb: 0.75 } }}>
              <li><Typography variant="body2">⚙️ <strong>Gear icon (top right)</strong> — opens your profile settings: edit your name, photo, email and privacy preferences.</Typography></li>
              <li><Typography variant="body2">🌐 <strong>Public Page button (top right)</strong> — view the public rankings, results and records visible to everyone.</Typography></li>
              <li><Typography variant="body2">🔑 <strong>Change Password</strong> — available inside your profile settings (gear icon). Use this to set a personal password.</Typography></li>
            </Box>
            <Typography variant="overline" sx={{ fontWeight: "bold", color: "#743444", letterSpacing: 1 }}>🎮 Actions</Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 2, "& li": { mb: 0.75 } }}>
              <li><Typography variant="body2">📋 <strong>Register for League Days</strong> — you can register yourself for upcoming league days here.</Typography></li>
              <li><Typography variant="body2">🏸 <strong>Register for Tournaments</strong> — if there are available tournaments, you can register yourself for those tournaments here.</Typography></li>
            </Box>
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Good luck! 🎾
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setWelcomeDialogHidden(true)}>Read later</Button>
          <Button variant="contained" onClick={handleWelcomeRead}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Policy Acceptance Dialog */}
      <PrivacyPolicyAcceptanceDialog
        open={showPrivacyDialog}
        onAccept={handlePrivacyAccept}
        onDecline={handlePrivacyDecline}
      />

      {/* Email Collection Dialog */}
      <Dialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>📧</span>
            <span>Add Your Email Address</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide your email address for the following:
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Automated password reset and account recovery
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Season registration confirmations (if you choose to participate)
            </Typography>
            <Typography component="li" variant="body2">
              Important league and tournament updates
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Your email will be stored according to our privacy policy.
          </Typography>
          {emailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailError}
            </Alert>
          )}
          <TextField
            fullWidth
            type="email"
            label="Email Address"
            placeholder="your.email@example.com"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              setEmailError("");
            }}
            disabled={isUpdatingEmail}
            autoFocus
            helperText="Required for password reset and season confirmations"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowEmailDialog(false)}
            disabled={isUpdatingEmail}
          >
            Skip for Now
          </Button>
          <Button
            onClick={async () => {
              setEmailError("");

              if (!emailInput) {
                setEmailError("Email is required");
                return;
              }

              // Basic email validation
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(emailInput)) {
                setEmailError("Please enter a valid email address");
                return;
              }

              setIsUpdatingEmail(true);

              try {
                const response = await apiRequest("/api/player/me/email", {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${playerToken}`,
                  },
                  body: JSON.stringify({ email: emailInput }),
                });

                if (response.ok) {
                  // Update local player state
                  if (player) {
                    setPlayer({ ...player, email: emailInput });
                  }
                  setShowEmailDialog(false);
                  setEmailInput("");
                } else {
                  const data = await response.json();
                  setEmailError(data.error || "Failed to update email");
                }
              } catch (error) {
                console.error("Error updating email:", error);
                setEmailError("Failed to update email");
              } finally {
                setIsUpdatingEmail(false);
              }
            }}
            variant="contained"
            disabled={isUpdatingEmail}
          >
            {isUpdatingEmail ? <CircularProgress size={24} /> : "Save Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Badge Celebration Dialog */}
      {player && (
        <BadgeCelebration
          open={showBadgeCelebration}
          onClose={handleBadgeCelebrationClose}
          playerName={player.name}
          newBadges={unseenBadges}
        />
      )}

      {/* Discount Code Gift Dialog */}
      <DiscountCodeGiftDialog />

      {/* Pop-up Announcements */}
      {pendingPopups.length > 0 && currentPopupIndex < pendingPopups.length && (() => {
        const popup = pendingPopups[currentPopupIndex];
        const styleConfig = POPUP_STYLE_CONFIG[popup.style || 'notice'];
        return (
          <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown PaperProps={{ sx: { overflow: 'hidden' } }}>
            <PopupHeader
              style={popup.style || 'notice'}
              title={popup.title}
              imageUrl={popup.imageUrl}
              size="hero"
              badge={
                pendingPopups.length > 1 ? (
                  <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8, opacity: 0.8 }}>
                    ({currentPopupIndex + 1} of {pendingPopups.length})
                  </span>
                ) : undefined
              }
            />
            <DialogContent sx={{ pt: 3 }}>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{popup.message}</p>
            </DialogContent>
            <DialogActions>
              {popup.linkUrl && (
                <button
                  onClick={() => openPopupLink(popup.linkUrl)}
                  style={{
                    background: '#2e7d32', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', marginRight: 8,
                  }}
                >
                  {popup.linkLabel || 'Open link'}
                </button>
              )}
              <button
                onClick={() => acknowledgePopup(popup.id)}
                style={{
                  background: styleConfig.buttonColor, color: '#fff', border: 'none',
                  borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem'
                }}
              >
                Got it, I've read this ✓
              </button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Received Tournament Invitation Popups — shown after announcement popups are cleared */}
      {(pendingPopups.length === 0 || currentPopupIndex >= pendingPopups.length) &&
        receivedInvites.length > 0 &&
        invitePopupIndex < receivedInvites.length &&
        (() => {
          const invite = receivedInvites[invitePopupIndex];
          const isProcessing = processingInviteId === invite.id;
          const dismiss = () => setInvitePopupIndex((i) => i + 1);
          return (
            <Dialog open maxWidth="xs" fullWidth onClose={dismiss}>
              <DialogTitle sx={{ pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    You've been invited!
                  </Typography>
                  {receivedInvites.length > 1 && (
                    <Typography variant="caption" sx={{ opacity: 0.55 }}>
                      {invitePopupIndex + 1}/{receivedInvites.length}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={dismiss} sx={{ ml: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ pt: 1.5, pb: 0 }}>
                {/* Gradient hero */}
                <Box
                  sx={{
                    borderRadius: 2,
                    background: brandGradient,
                    color: 'white',
                    p: 2.5,
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  <Avatar
                    src={invite.senderProfilePicture || undefined}
                    sx={{
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 1,
                      border: '3px solid rgba(255,255,255,0.6)',
                      fontSize: '1.5rem',
                      bgcolor: 'rgba(255,255,255,0.25)',
                    }}
                  >
                    {invite.senderPlayerName?.charAt(0) ?? '?'}
                  </Avatar>
                  <Typography variant="body2" sx={{ opacity: 0.88, mb: 0.5 }}>
                    <strong>{invite.senderPlayerName}</strong> wants to team up with you
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {invite.tournamentName}
                  </Typography>
                  {invite.teamName && (
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      Team: {invite.teamName}
                    </Typography>
                  )}
                </Box>

                {invite.personalMessage && (
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
                    <Typography variant="body2" fontStyle="italic" color="text.secondary">
                      "{invite.personalMessage}"
                    </Typography>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary">
                  Not ready to decide? You can respond later from the{' '}
                  <span
                    style={{ color: primaryColor, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate('/view-tournaments')}
                  >
                    tournaments page
                  </span>.
                </Typography>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={dismiss}
                  sx={{ mr: 'auto', color: 'text.secondary' }}
                >
                  Decide Later
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDeclineInvite(invite.id)}
                  disabled={isProcessing}
                >
                  {isProcessing ? <CircularProgress size={16} color="inherit" /> : 'Decline'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleAcceptInvite(invite.id)}
                  disabled={isProcessing}
                >
                  {isProcessing ? <CircularProgress size={16} color="inherit" /> : 'Accept'}
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}

      <Snackbar
        open={!!inviteError}
        autoHideDuration={6000}
        onClose={() => setInviteError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Alert severity="error" variant="filled" onClose={() => setInviteError(null)} sx={{ width: "100%" }}>
          {inviteError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PlayerDashboard;
